import React, { useState, useEffect, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import './SchedulerPage.css';
import sessionService from '../api/sessionService';
import trainerService from '../api/trainerService';
import batchScheduleService from '../api/batchScheduleService';
import holidayService from '../api/holidayService';
import leaveService from '../api/leaveService';
import authService from '../api/authService';
import trainerTaskService from '../api/trainerTaskService';
import ZoneWeeklyView from './ZoneWeeklyView';
import { textWithBreaks } from '../utils/sanitize';
import { parseLocalDate } from '../utils/dateUtils';

// Helper function to format session data for FullCalendar
const formatEvents = (sessions) => {
  if (!Array.isArray(sessions)) return [];
  return sessions.map(session => ({
    id: session.id,
    title: `${session.batch?.program?.program_name || 'N/A'} - ${session.module?.name || 'N/A'}`,
    start: session.session_date,
    className: `event-${session.status?.toLowerCase() || 'draft'}`,
    extendedProps: { ...session }
  }));
};

const SchedulerPage = () => {
  // Component State
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [batches, setBatches] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [availableModules, setAvailableModules] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const initialFormState = { id: null, batchId: '', moduleId: '', trainerId: '', session_date: '', status: 'Draft' };
  const [formData, setFormData] = useState(initialFormState);
  
  // New state for batch-specific scheduling
  const [selectedBatchId, setSelectedBatchId] = useState('');
  const [viewMode, setViewMode] = useState('all'); // 'all' or 'batch-specific'
  const [batchScheduleData, setBatchScheduleData] = useState([]);
  const [showBulkUpload, setShowBulkUpload] = useState(false);

  // View tab state (calendar | table | week | trainer | center | zone)
  const [viewTab, setViewTab] = useState('calendar');
  const [trainerWiseData, setTrainerWiseData] = useState(null);
  const [centerWiseData, setCenterWiseData] = useState(null);
  const [zoneWiseData, setZoneWiseData] = useState(null);
  const [zoneFilters, setZoneFilters] = useState({ includeCompleted: false, includeUpcoming: false });

  // Insert-session (F1) state
  const [insertModal, setInsertModal] = useState(null); // { week, day } when open, null when closed
  const [insertForm, setInsertForm] = useState({ session_content: '', trainerId: '' });
  const [insertPreview, setInsertPreview] = useState(null); // { affectedCount, needsExtension, suggestedEndDate, ... }
  const [insertSubmitting, setInsertSubmitting] = useState(false);
  const [insertError, setInsertError] = useState('');
  const [csvData, setCsvData] = useState('');
  const [parsedCsvForTable, setParsedCsvForTable] = useState(null);
  const [batchCsvData, setBatchCsvData] = useState({});
  const [batchProgress, setBatchProgress] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [parsedExcelData, setParsedExcelData] = useState(null);
  const [excelError, setExcelError] = useState(null);
  const [excelSheetNames, setExcelSheetNames] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState('');

  // Holiday management state
  const [showHolidayModal, setShowHolidayModal] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [holidayYear, setHolidayYear] = useState(new Date().getFullYear());
  const [holidayCsvData, setHolidayCsvData] = useState('');
  const [holidayFile, setHolidayFile] = useState(null);
  const [holidayDates, setHolidayDates] = useState(new Set());

  // Facilitator assignment state (merged from ScheduleTable)
  const [trainerAssignments, setTrainerAssignments] = useState({}); // Assignments by date for conflict detection
  const [trainerLeaves, setTrainerLeaves] = useState({}); // Approved leaves by date for conflict detection
  const [trainerTasksByDate, setTrainerTasksByDate] = useState({}); // Tasks by date for conflict detection
  const [savingSession, setSavingSession] = useState(null); // Track which session is being saved

  // Batch schedule edit modal state
  const [showScheduleEditModal, setShowScheduleEditModal] = useState(false);
  const [selectedScheduleEntry, setSelectedScheduleEntry] = useState(null);

  // Zone weekly view state
  const [zoneWeekStart, setZoneWeekStart] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(today);
    monday.setDate(diff);
    return monday.getFullYear() + '-' +
      String(monday.getMonth() + 1).padStart(2, '0') + '-' +
      String(monday.getDate()).padStart(2, '0');
  });
  const [trainerTasks, setTrainerTasks] = useState([]);

  // Default Indian Holidays (generates for the selected year)
  const getIndianHolidays = (year) => {
    return [
      { date: `${year}-01-26`, name: 'Republic Day', description: 'National Holiday' },
      { date: `${year}-03-08`, name: 'Maha Shivaratri', description: 'Hindu Festival' },
      { date: `${year}-03-14`, name: 'Holi', description: 'Festival of Colors' },
      { date: `${year}-03-29`, name: 'Good Friday', description: 'Christian Holiday' },
      { date: `${year}-04-10`, name: 'Ram Navami', description: 'Hindu Festival' },
      { date: `${year}-04-14`, name: 'Ambedkar Jayanti', description: 'National Holiday' },
      { date: `${year}-04-21`, name: 'Mahavir Jayanti', description: 'Jain Festival' },
      { date: `${year}-05-01`, name: 'May Day', description: 'Labour Day' },
      { date: `${year}-05-12`, name: 'Buddha Purnima', description: 'Buddhist Festival' },
      { date: `${year}-06-17`, name: 'Eid ul-Fitr', description: 'Islamic Festival' },
      { date: `${year}-07-17`, name: 'Muharram', description: 'Islamic Observance' },
      { date: `${year}-08-15`, name: 'Independence Day', description: 'National Holiday' },
      { date: `${year}-08-26`, name: 'Janmashtami', description: 'Hindu Festival' },
      { date: `${year}-09-16`, name: 'Milad un-Nabi', description: 'Islamic Festival' },
      { date: `${year}-10-02`, name: 'Gandhi Jayanti', description: 'National Holiday' },
      { date: `${year}-10-12`, name: 'Dussehra', description: 'Hindu Festival' },
      { date: `${year}-10-20`, name: 'Eid ul-Adha', description: 'Islamic Festival' },
      { date: `${year}-10-31`, name: 'Diwali', description: 'Festival of Lights' },
      { date: `${year}-11-01`, name: 'Diwali Holiday', description: 'Festival of Lights' },
      { date: `${year}-11-15`, name: 'Guru Nanak Jayanti', description: 'Sikh Festival' },
      { date: `${year}-12-25`, name: 'Christmas', description: 'Christian Holiday' }
    ];
  };

  const loadIndianHolidays = () => {
    const indianHolidays = getIndianHolidays(holidayYear);
    const csvLines = ['Date,Name,Description'];
    indianHolidays.forEach(h => {
      csvLines.push(`${h.date},${h.name},${h.description}`);
    });
    setHolidayCsvData(csvLines.join('\n'));
  };

  // Permissions
  const currentUser = authService.getCurrentUser();
  const isScheduler = currentUser && currentUser.role === 'scheduler';

  // Helper to normalize date to YYYY-MM-DD format
  const normalizeDateKey = (dateStr) => {
    if (!dateStr) return null;
    // Handle both "2026-01-30" and "2026-01-30T00:00:00.000Z" formats
    return String(dateStr).split('T')[0];
  };

  // Load batch CSV data from localStorage on component mount
  useEffect(() => {
    const savedBatchData = localStorage.getItem('batchCsvData');
    if (savedBatchData) {
      try {
        const parsed = JSON.parse(savedBatchData);
        setBatchCsvData(parsed);
      } catch (error) {
        console.error('Error loading saved batch data:', error);
      }
    }
  }, []);

  // Save batch CSV data to localStorage whenever it changes
  useEffect(() => {
    if (Object.keys(batchCsvData).length > 0) {
      localStorage.setItem('batchCsvData', JSON.stringify(batchCsvData));
    }
  }, [batchCsvData]);

  // Update table data when batch selection changes
  useEffect(() => {
    if (selectedBatchId && batchCsvData[selectedBatchId]) {
      setParsedCsvForTable(batchCsvData[selectedBatchId]);
    } else {
      setParsedCsvForTable(null);
    }
  }, [selectedBatchId, batchCsvData]);

  // --- DATA FETCHING ---
  const fetchAllData = useCallback(() => {
    setLoading(true);
    Promise.allSettled([
      sessionService.getAllSessions(),
      batchScheduleService.getAllBatches(),
      trainerService.getAllTrainers(),
    ]).then(([sessionsRes, batchesRes, trainersRes]) => {
      if (sessionsRes.status === 'fulfilled') setEvents(formatEvents(sessionsRes.value.data));
      if (batchesRes.status === 'fulfilled') setBatches(batchesRes.value.data.data || batchesRes.value.data);
      if (trainersRes.status === 'fulfilled') setTrainers(trainersRes.value.data);
    }).catch(err => console.error("A critical error occurred while fetching data:", err))
      .finally(() => setLoading(false));
  }, []);

  const fetchBatchSchedule = useCallback(async (batchId) => {
    setLoading(true);
    try {
      const response = await batchScheduleService.getBatchSchedule(batchId);
      const scheduleEntries = response.data.data || [];
      setBatchScheduleData(scheduleEntries);

      // Fetch trainer assignments for conflict detection
      const dates = scheduleEntries
        .map(e => e.session_date)
        .filter(d => d); // Filter out null dates

      if (dates.length > 0) {
        const sortedDates = dates.sort();
        const startDate = normalizeDateKey(sortedDates[0]);
        const endDate = normalizeDateKey(sortedDates[sortedDates.length - 1]);

        try {
          // Fetch trainer assignments, approved leaves, and trainer tasks in parallel
          const [assignmentsRes, leavesRes, tasksRes] = await Promise.all([
            batchScheduleService.getTrainerAssignments(startDate, endDate),
            leaveService.getApprovedLeavesInRange(startDate, endDate),
            trainerTaskService.getTrainerTasks(startDate, endDate)
          ]);

          if (assignmentsRes.data.success) {
            setTrainerAssignments(assignmentsRes.data.data || {});
          }

          if (leavesRes.data.success) {
            setTrainerLeaves(leavesRes.data.data || {});
          }

          if (tasksRes.data.success) {
            // Group tasks by date
            const taskMap = {};
            (tasksRes.data.data || []).forEach(task => {
              const dateKey = normalizeDateKey(task.task_date);
              if (!taskMap[dateKey]) taskMap[dateKey] = [];
              taskMap[dateKey].push(task);
            });
            setTrainerTasksByDate(taskMap);
          }
        } catch (assignErr) {
          console.error('Error fetching trainer assignments/leaves:', assignErr);
          setTrainerAssignments({});
          setTrainerLeaves({});
          setTrainerTasksByDate({});
        }
      } else {
        setTrainerAssignments({});
        setTrainerLeaves({});
        setTrainerTasksByDate({});
      }

      const selectedBatch = batches.find(b => b.id === parseInt(batchId));

      if (selectedBatch) {
        const scheduleEvents = batchScheduleService.formatScheduleForCalendar(
          scheduleEntries,
          selectedBatch.start_date,
          holidayDates
        );
        setEvents(scheduleEvents);
      } else {
        // Fallback: use today's date if batch not found
        const scheduleEvents = batchScheduleService.formatScheduleForCalendar(
          scheduleEntries,
          new Date().toISOString(),
          holidayDates
        );
        setEvents(scheduleEvents);
      }
    } catch (err) {
      console.error("Error fetching batch schedule:", err);
      setTrainerAssignments({});
      setTrainerLeaves({});
    } finally {
      setLoading(false);
    }
  }, [batches, holidayDates]);

  // Fetch holidays for a given year
  const fetchHolidays = useCallback((year) => {
    holidayService.getAllHolidays(year)
      .then(response => {
        if (response.data.success) {
          setHolidays(response.data.data);
          // Create a Set of holiday dates for quick lookup
          const dates = new Set(response.data.data.map(h => h.date));
          setHolidayDates(dates);
        }
      })
      .catch(err => console.error('Error fetching holidays:', err));
  }, []);

  // Holiday upload handlers
  const handleHolidayFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setHolidayFile(file);

    try {
      if (file.name.endsWith('.csv')) {
        const text = await file.text();
        setHolidayCsvData(text);
      } else if (file.name.match(/\.xlsx?$/)) {
        const parsedHolidays = await holidayService.parseHolidayExcel(file);
        // Convert to CSV format for display
        const csvLines = ['Date,Name,Description'];
        parsedHolidays.forEach(h => {
          csvLines.push(`${h.date},${h.name},${h.description || ''}`);
        });
        setHolidayCsvData(csvLines.join('\n'));
      }
    } catch (error) {
      console.error('Error parsing holiday file:', error);
      alert('Error parsing file: ' + error.message);
    }
  };

  const handleHolidayUpload = async () => {
    if (!holidayCsvData.trim()) {
      alert('Please select a file or paste holiday data');
      return;
    }

    try {
      setLoading(true);
      const holidays = holidayService.parseHolidayCsv(holidayCsvData);

      if (holidays.length === 0) {
        alert('No valid holidays found. Format: date,name,description');
        return;
      }

      const response = await holidayService.bulkUploadHolidays(holidays, holidayYear, true);

      if (response.data.success) {
        // Recalculate session dates to account for new holidays
        try {
          await batchScheduleService.recalculateSessionDates();
        } catch (recalcError) {
          console.error('Error recalculating session dates:', recalcError);
          // Don't fail the whole operation if recalculation fails
        }

        alert(`Successfully uploaded ${response.data.count} holidays! Session dates have been recalculated.`);
        fetchHolidays(holidayYear);
        setHolidayCsvData('');
        setHolidayFile(null);
        setShowHolidayModal(false);

        // Refresh the batch schedule if one is selected
        if (selectedBatchId) {
          fetchBatchSchedule(selectedBatchId);
        }
      }
    } catch (error) {
      console.error('Holiday upload error:', error);
      alert('Error uploading holidays: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteHoliday = async (id) => {
    if (!window.confirm('Are you sure you want to delete this holiday? Session dates will be recalculated.')) return;

    try {
      setLoading(true);
      await holidayService.deleteHoliday(id);

      // Recalculate session dates after holiday deletion
      try {
        await batchScheduleService.recalculateSessionDates();
      } catch (recalcError) {
        console.error('Error recalculating session dates:', recalcError);
      }

      fetchHolidays(holidayYear);

      // Refresh the batch schedule if one is selected
      if (selectedBatchId) {
        fetchBatchSchedule(selectedBatchId);
      }
    } catch (error) {
      alert('Error deleting holiday: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
    fetchHolidays(holidayYear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - run only once on mount

  useEffect(() => {
    if (viewMode === 'batch-specific' && selectedBatchId) {
      fetchBatchSchedule(selectedBatchId);
    } else if (viewMode === 'all') {
      fetchAllData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, selectedBatchId]); // Remove fetchBatchSchedule and fetchAllData from dependencies

  // NOTE: localStorage loading disabled - data is now saved to database
  // The useEffect for loadSavedCalendarEvents has been removed to prevent duplicate events
  // Database events are loaded via fetchBatchSchedule

  // Fetch batch progress when a batch is selected
  useEffect(() => {
    if (selectedBatchId) {
      batchScheduleService.getBatchProgress(selectedBatchId)
        .then(response => {
          if (response.data.success) {
            setBatchProgress(response.data.data);
          }
        })
        .catch(err => console.error("Error fetching batch progress:", err));
    } else {
      setBatchProgress(null);
    }
  }, [selectedBatchId, batchScheduleData]);


  // --- BATCH SELECTION HANDLERS ---
  const handleBatchChange = (e) => {
    const batchId = e.target.value;
    setSelectedBatchId(batchId);

    if (batchId) {
      setViewMode('batch-specific');
    } else {
      setViewMode('all');
    }
  };

  // Handle Excel file selection
  const handleExcelFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls'].includes(fileExtension)) {
      setExcelError('Please select an Excel file (.xlsx or .xls)');
      return;
    }

    setUploadedFile(file);
    setExcelError(null);
    setExcelSheetNames([]);
    setSelectedSheet('');
    setParsedExcelData(null);
    setCsvData('');

    try {
      // Parse with first sheet by default, which also returns sheet names
      const parsedData = await batchScheduleService.parseExcelFile(file);
      const sheetNames = parsedData.allSheetNames || [parsedData.sheetName];

      setParsedExcelData(parsedData);
      const csvFromExcel = batchScheduleService.excelDataToCsv(parsedData);
      setCsvData(csvFromExcel);

      if (sheetNames.length > 1) {
        // Multiple sheets available - show selector
        setExcelSheetNames(sheetNames);
        setSelectedSheet(parsedData.sheetName);
      }
    } catch (error) {
      console.error('Error reading Excel file:', error);
      setExcelError(`Error reading Excel file: ${error.message}`);
    }
  };

  // Parse a specific sheet from the Excel file
  const parseExcelSheet = async (file, sheetName) => {
    try {
      const parsedData = await batchScheduleService.parseExcelFile(file, sheetName);
      setParsedExcelData(parsedData);

      // Convert to CSV format for preview and compatibility with existing flow
      const csvFromExcel = batchScheduleService.excelDataToCsv(parsedData);
      setCsvData(csvFromExcel);
    } catch (error) {
      console.error('Error parsing Excel sheet:', error);
      setExcelError(`Error parsing Excel sheet: ${error.message}`);
      setParsedExcelData(null);
    }
  };

  // Handle sheet selection change
  const handleSheetSelect = async (sheetName) => {
    setSelectedSheet(sheetName);
    if (uploadedFile && sheetName) {
      await parseExcelSheet(uploadedFile, sheetName);
    }
  };

  // Clear Excel file selection
  const handleClearExcelFile = () => {
    setUploadedFile(null);
    setParsedExcelData(null);
    setExcelError(null);
    setExcelSheetNames([]);
    setSelectedSheet('');
    setCsvData('');
    // Reset file input
    const fileInput = document.getElementById('excel-file-input');
    if (fileInput) fileInput.value = '';
  };

  // --- BULK UPLOAD HANDLERS ---
  const resetBulkUploadState = () => {
    setCsvData('');
    setUploadedFile(null);
    setParsedExcelData(null);
    setExcelError(null);
    setExcelSheetNames([]);
    setSelectedSheet('');
    // Reset file input DOM element so the same file can be selected again
    const fileInput = document.getElementById('excel-file-input');
    if (fileInput) fileInput.value = '';
  };

  const handleBulkUpload = () => {
    if (!selectedBatchId) {
      alert('Please select a batch first');
      return;
    }
    resetBulkUploadState();
    setShowBulkUpload(true);
  };

  // Recalculate session dates for all batches
  const handleRecalculateDates = async () => {
    if (!window.confirm(
      'This will recalculate framework-based session dates for all batches based on their start dates and holidays.\n\n' +
      'Sessions that were manually rearranged by trainers will NOT be affected — their dates are protected.\n\n' +
      'Continue?'
    )) {
      return;
    }
    try {
      setLoading(true);
      const result = await batchScheduleService.recalculateSessionDates();
      const data = result?.data;
      const totalUpdated = data?.totalUpdated || 0;
      const totalSkipped = (data?.batchResults || []).reduce((sum, b) => sum + (b.skippedManual || 0), 0);
      let msg = `Session dates recalculated. ${totalUpdated} updated.`;
      if (totalSkipped > 0) {
        msg += `\n${totalSkipped} manually-edited session(s) were left unchanged.`;
      }
      alert(msg);
      // Reload batch schedule data if a batch is selected
      if (selectedBatchId) {
        const response = await batchScheduleService.getBatchSchedule(selectedBatchId);
        setBatchScheduleData(response.data.data || []);
      }
    } catch (error) {
      console.error('Error recalculating dates:', error);
      alert('Failed to recalculate dates: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCloseBulkUpload = () => {
    resetBulkUploadState();
    setShowBulkUpload(false);
  };

  const handleCsvUpload = async () => {
    if (!csvData.trim() || !selectedBatchId) {
      alert('Please select a batch and enter CSV data');
      return;
    }

    try {
      setLoading(true);

      // Check if CSV data is valid before parsing
      if (!csvData || csvData.trim().length === 0) {
        alert('Please paste valid CSV data');
        return;
      }

      // Custom CSV parsing that handles multi-line cells like Gemini
      const processedData = parseExcelCsvWithContinuationRows(csvData.trim());

      // Check if parsing was successful
      if (!processedData || processedData.length === 0) {
        alert('Failed to parse CSV data. Please check the format and try again.');
        console.error('CSV parsing failed - no data returned');
        return;
      }

      // Convert processed data to API format for database storage
      // First collect flat entries, then group by week
      const flatEntries = [];
      processedData.forEach(row => {
        // Get the day number from the first column (Day 1, Day 2, etc.)
        const dayKey = Object.keys(row)[0];
        const dayMatch = row[dayKey]?.match?.(/day\s*(\d+)/i) || dayKey?.match?.(/day\s*(\d+)/i);
        const dayNumber = dayMatch ? parseInt(dayMatch[1]) : null;

        if (!dayNumber) return;

        // Process each week column
        Object.keys(row).forEach(key => {
          const weekMatch = key.match(/week\s*(\d+)/i);
          if (weekMatch && row[key]) {
            const weekNumber = parseInt(weekMatch[1]);
            const content = row[key];

            // Split by <br> if there are multiple sessions
            const sessions = content.split(/<br\s*\/?>/i).filter(s => s.trim());

            sessions.forEach(session => {
              flatEntries.push({
                week: weekNumber,
                day: dayNumber,
                content: session.trim()
              });
            });
          }
        });
      });

      if (flatEntries.length === 0) {
        alert('No valid schedule entries found. Please check the format.');
        return;
      }

      // Group by week for API format: [{week: 1, days: [{day: 1, content: "..."}, ...]}, ...]
      const weekMap = {};
      flatEntries.forEach(entry => {
        if (!weekMap[entry.week]) {
          weekMap[entry.week] = { week: entry.week, days: [] };
        }
        weekMap[entry.week].days.push({
          day: entry.day,
          content: entry.content
        });
      });
      const scheduleData = Object.values(weekMap);

      // Save directly to database
      const response = await batchScheduleService.bulkUploadSchedule(selectedBatchId, scheduleData);

      if (response.data.success) {

        // Recalculate session dates based on batch start date and holidays
        try {
          await batchScheduleService.recalculateSessionDates(selectedBatchId);
        } catch (dateErr) {
          console.error('Error recalculating session dates:', dateErr);
        }

        // Store CSV data for table view as well
        setBatchCsvData(prev => ({
          ...prev,
          [selectedBatchId]: processedData
        }));

        // Update current table view
        setParsedCsvForTable(processedData);

        // Refresh the batch schedule to load from database
        fetchBatchSchedule(selectedBatchId);

        alert(`Schedule uploaded and saved successfully! ${response.data.count || flatEntries.length} sessions saved.`);
        handleCloseBulkUpload();
      } else {
        alert('Failed to save schedule: ' + (response.data.message || 'Unknown error'));
      }

    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload schedule: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // --- CALENDAR INTERACTION HANDLERS ---
  const handleDateSelect = (selectInfo) => {
    if (!isScheduler) return;

    // Check if batch has CSV data uploaded
    const batchCsvData = JSON.parse(localStorage.getItem('batchCsvData') || '{}');
    const hasCsvData = selectedBatchId && batchCsvData[selectedBatchId];

    if (hasCsvData) {
      // New feature: Auto-populate calendar from CSV data
      handleAutoPopulateSchedule(selectInfo.startStr);
    } else {
      // Fallback: Show message that CSV data is required
      alert(' Please upload CSV schedule data in Table View first to auto-populate the calendar.');
    }
  };

  // --- FACILITATOR ASSIGNMENT HELPERS (merged from ScheduleTable) ---

  // Get the current batch's info (id and timing type)
  const getCurrentBatchInfo = () => {
    const selectedBatch = batches.find(b => b.id === parseInt(selectedBatchId));
    return {
      id: selectedBatch?.id,
      batchType: selectedBatch?.batch_type, // Don't default - keep actual value for comparison
      startTime: selectedBatch?.start_time,
      endTime: selectedBatch?.end_time
    };
  };

  // Helper: Convert time string (e.g., "10:00 AM", "02:30 PM") to minutes from midnight
  const timeToMinutes = (timeStr) => {
    if (!timeStr) return null;
    const cleanTime = timeStr.trim().toUpperCase();
    const isPM = cleanTime.includes('PM');
    const isAM = cleanTime.includes('AM');
    const timePart = cleanTime.replace(/\s*(AM|PM)\s*/i, '');
    const [hoursStr, minutesStr] = timePart.split(':');
    let hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr || '0', 10);
    if (isPM && hours !== 12) hours += 12;
    if (isAM && hours === 12) hours = 0;
    return hours * 60 + minutes;
  };

  // Helper: Check if two time ranges overlap
  const doTimesOverlap = (start1, end1, start2, end2) => {
    const s1 = timeToMinutes(start1);
    const e1 = timeToMinutes(end1);
    const s2 = timeToMinutes(start2);
    const e2 = timeToMinutes(end2);
    if (s1 === null || e1 === null || s2 === null || e2 === null) return false;
    return s1 < e2 && s2 < e1;
  };

  // Check if two batch types/times conflict (overlapping time slots)
  // Returns true if they conflict, false if they can run in parallel
  const doBatchTypesConflict = (type1, type2, times1 = {}, times2 = {}) => {
    // If either is null/undefined, we can't determine - assume no conflict
    if (!type1 || !type2) {
      return false;
    }

    // If both are predefined types (morning/afternoon), simple comparison
    if (type1 !== 'custom' && type2 !== 'custom') {
      return type1 === type2;
    }

    // If either is custom, compare actual times for overlap
    const start1 = times1.startTime || (type1 === 'afternoon' ? '02:00 PM' : '10:00 AM');
    const end1 = times1.endTime || (type1 === 'afternoon' ? '05:00 PM' : '01:00 PM');
    const start2 = times2.startTime || (type2 === 'afternoon' ? '02:00 PM' : '10:00 AM');
    const end2 = times2.endTime || (type2 === 'afternoon' ? '05:00 PM' : '01:00 PM');

    return doTimesOverlap(start1, end1, start2, end2);
  };

  // Get available facilitators for a specific session
  // Filters out trainers who are:
  // 1. Already assigned to OTHER sessions on the same date AND same time slot (different batch, same batch_type)
  // 2. On approved leave on that date
  // Note: A trainer CAN teach morning batch AND afternoon batch on the same day
  const getAvailableFacilitators = (sessionDate, currentSessionId) => {
    if (!sessionDate) {
      return trainers;
    }

    // Normalize date to YYYY-MM-DD format for lookup
    const dateKey = normalizeDateKey(sessionDate);
    const currentBatch = getCurrentBatchInfo();

    // Get trainer IDs already assigned on this date with overlapping time slot (excluding current session)
    // A trainer can teach morning AND afternoon on the same day, but NOT overlapping time slots
    const assignedOnDate = trainerAssignments[dateKey]
      ? trainerAssignments[dateKey]
          .filter(a => {
            // Exclude current session being edited
            if (a.scheduleId === currentSessionId) return false;

            // If it's the same batch, don't count as conflict (can have multiple sessions in same batch)
            if (a.batchId === currentBatch.id) return false;

            // Check if the batch times conflict (overlapping time slots)
            return doBatchTypesConflict(
              a.batchType,
              currentBatch.batchType,
              { startTime: a.startTime, endTime: a.endTime },
              { startTime: currentBatch.startTime, endTime: currentBatch.endTime }
            );
          })
          .map(a => a.trainerId)
      : [];

    // Get trainer IDs on approved leave on this date
    const onLeaveOnDate = trainerLeaves[dateKey]
      ? trainerLeaves[dateKey].map(l => l.trainerId)
      : [];

    // Get trainer IDs with conflicting tasks on this date
    const withTaskOnDate = trainerTasksByDate[dateKey]
      ? trainerTasksByDate[dateKey]
          .filter(task => {
            // Full-day task conflicts with any session
            if (task.duration === 'full_day') return true;
            // Morning task conflicts with morning/custom sessions
            if (task.duration === 'morning' && (currentBatch.batchType === 'morning' || currentBatch.batchType === 'custom')) return true;
            // Afternoon task conflicts with afternoon/custom sessions
            if (task.duration === 'afternoon' && (currentBatch.batchType === 'afternoon' || currentBatch.batchType === 'custom')) return true;
            return false;
          })
          .map(t => t.trainerId)
      : [];

    // Combine all sets of unavailable trainers
    const unavailableTrainerIds = new Set([...assignedOnDate, ...onLeaveOnDate, ...withTaskOnDate]);

    // Return trainers who are available (not assigned to same time slot and not on leave)
    return trainers.filter(t => !unavailableTrainerIds.has(t.id));
  };

  // Get count of trainers on leave for a specific date
  const getTrainersOnLeaveCount = (sessionDate) => {
    const dateKey = normalizeDateKey(sessionDate);
    if (!dateKey || !trainerLeaves[dateKey]) return 0;
    return trainerLeaves[dateKey].length;
  };

  // Get names of trainers on leave for a specific date
  const getTrainersOnLeaveNames = (sessionDate) => {
    const dateKey = normalizeDateKey(sessionDate);
    if (!dateKey || !trainerLeaves[dateKey]) return [];
    return trainerLeaves[dateKey].map(l => l.trainerName).filter(Boolean);
  };

  // Handle facilitator assignment
  const handleFacilitatorChange = async (sessionId, trainerId) => {
    setSavingSession(sessionId);
    try {
      await batchScheduleService.updateScheduleEntry(sessionId, {
        trainerId: trainerId ? parseInt(trainerId) : null
      });
      await fetchBatchSchedule(selectedBatchId);
      // Invalidate other views so they refetch on next switch
      setTrainerWiseData(null);
      setCenterWiseData(null);
      setZoneWiseData(null);
    } catch (error) {
      console.error('Error assigning facilitator:', error);
      const errorMsg = error.response?.data?.message || 'Failed to assign facilitator. Please try again.';
      alert(errorMsg);
    } finally {
      setSavingSession(null);
    }
  };

  // Handle facilitator assignment from center-wise view
  const handleCenterFacilitatorChange = async (sessionId, trainerId) => {
    setSavingSession(sessionId);
    try {
      await batchScheduleService.updateScheduleEntry(sessionId, {
        trainerId: trainerId ? parseInt(trainerId) : null
      });
      // Reload center-wise data to reflect changes
      await fetchCenterWiseData();
    } catch (error) {
      console.error('Error assigning facilitator:', error);
      const errorMsg = error.response?.data?.message || 'Failed to assign facilitator. Please try again.';
      alert(errorMsg);
    } finally {
      setSavingSession(null);
    }
  };

  // Handle facilitator assignment from zone-wise view
  const handleZoneFacilitatorChange = async (sessionId, trainerId) => {
    setSavingSession(sessionId);
    try {
      await batchScheduleService.updateScheduleEntry(sessionId, {
        trainerId: trainerId ? parseInt(trainerId) : null
      });
      await fetchZoneWiseData();
      // Invalidate other views so they refetch on next switch
      setTrainerWiseData(null);
      setCenterWiseData(null);
      if (selectedBatchId) setBatchScheduleData([]);
    } catch (error) {
      console.error('Error assigning facilitator:', error);
      const errorMsg = error.response?.data?.message || 'Failed to assign facilitator. Please try again.';
      alert(errorMsg);
    } finally {
      setSavingSession(null);
    }
  };

  // Handle schedule entry update from modal (facilitator + status)
  const handleScheduleEntryUpdate = async (updates) => {
    if (!selectedScheduleEntry) return;

    setSavingSession(selectedScheduleEntry.id);
    try {
      await batchScheduleService.updateScheduleEntry(selectedScheduleEntry.id, {
        trainerId: updates.trainerId ? parseInt(updates.trainerId) : null,
        status: updates.status || 'scheduled'
      });
      await fetchBatchSchedule(selectedBatchId);
      setShowScheduleEditModal(false);
      setSelectedScheduleEntry(null);
      // Invalidate other views so they refetch on next switch
      setTrainerWiseData(null);
      setCenterWiseData(null);
      setZoneWiseData(null);
    } catch (error) {
      console.error('Error updating schedule entry:', error);
      const errorMsg = error.response?.data?.message || 'Failed to update session. Please try again.';
      alert(errorMsg);
    } finally {
      setSavingSession(null);
    }
  };

  // Close schedule edit modal
  const handleCloseScheduleEditModal = () => {
    setShowScheduleEditModal(false);
    setSelectedScheduleEntry(null);
  };

  // --- HELPER FUNCTIONS FOR WORKING DAYS ---

  // Check if a date is the 2nd or 4th Saturday of the month
  const isNonWorkingSaturday = (date) => {
    // Check if it's a Saturday first
    if (date.getDay() !== 6) return false;
    
    // Get the first day of the month
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
    
    // Find the first Saturday of the month
    const firstSaturday = new Date(firstDay);
    const daysToFirstSaturday = (6 - firstDay.getDay() + 7) % 7;
    firstSaturday.setDate(1 + daysToFirstSaturday);
    
    // Calculate which Saturday this is
    const daysDiff = date.getDate() - firstSaturday.getDate();
    const saturdayNumber = Math.floor(daysDiff / 7) + 1;
    
    // Return true if it's 2nd or 4th Saturday
    return saturdayNumber === 2 || saturdayNumber === 4;
  };
  
  // Get the next working day, skipping 2nd and 4th Saturdays and Sundays
  const getNextWorkingDay = (date) => {
    const nextDay = new Date(date);
    nextDay.setDate(date.getDate() + 1);
    
    // Skip Sundays and non-working Saturdays
    while (nextDay.getDay() === 0 || isNonWorkingSaturday(nextDay)) {
      nextDay.setDate(nextDay.getDate() + 1);
    }
    
    return nextDay;
  };
  
  // Calculate the actual working date considering non-working Saturdays
  const calculateWorkingDate = (startDate, totalDaysOffset) => {
    let currentDate = new Date(startDate);
    let remainingDays = totalDaysOffset;
    
    // Add days one by one, skipping non-working days
    while (remainingDays > 0) {
      currentDate.setDate(currentDate.getDate() + 1);
      
      // Skip Sundays and non-working Saturdays
      if (currentDate.getDay() !== 0 && !isNonWorkingSaturday(currentDate)) {
        remainingDays--;
      }
    }
    
    return currentDate;
  };

  // --- AUTO-POPULATE CALENDAR FROM CSV DATA ---
  const handleAutoPopulateSchedule = async (startDate) => {
    // Get CSV data for current batch
    const batchCsvData = JSON.parse(localStorage.getItem('batchCsvData') || '{}');
    const csvData = batchCsvData[selectedBatchId];

    if (!csvData || csvData.length === 0) {
      alert(' No CSV data found for this batch');
      return;
    }

    // Convert CSV data to calendar events
    const calendarEvents = [];
    const startDateObj = new Date(startDate);

    // Get headers (weeks) from first row
    const headers = Object.keys(csvData[0]);
    const weekHeaders = headers.slice(1); // Skip first column (day labels)

    // Process each day (row)
    csvData.forEach((dayData, dayIndex) => {
      // Process each week (column)
      weekHeaders.forEach((weekHeader, weekIndex) => {
        const sessionContent = dayData[weekHeader];
        if (sessionContent && sessionContent.trim()) {
          // Calculate the total days offset from start date
          // Each week adds 5 working days (Mon-Fri), plus the day within the week
          const totalDaysOffset = (weekIndex * 5) + dayIndex;

          // Calculate the actual working date considering non-working Saturdays
          const eventDate = calculateWorkingDate(startDateObj, totalDaysOffset);

          // Check if the calculated date falls on a non-working Saturday
          if (isNonWorkingSaturday(eventDate)) {
            const workingDate = getNextWorkingDay(eventDate);
            eventDate.setTime(workingDate.getTime());
          }
          
          // Split multi-line sessions and create separate events
          const sessions = sessionContent.split('<br>').filter(s => s.trim());
          
          sessions.forEach((session, sessionIndex) => {
            if (session.trim()) {
              const eventTime = new Date(eventDate);
              eventTime.setHours(9 + sessionIndex, 0, 0, 0); // Start at 9 AM, increment by hour
              
              calendarEvents.push({
                id: `csv-${dayIndex}-${weekIndex}-${sessionIndex}`,
                title: session.trim(),
                start: eventTime.toISOString(),
                backgroundColor: '#6366f1',
                borderColor: '#4f46e5',
                extendedProps: {
                  type: 'csv-schedule',
                  week: weekHeader,
                  day: `Day ${dayIndex + 1}`,
                  batchId: selectedBatchId,
                  originalDate: eventDate.toDateString(),
                  isShifted: isNonWorkingSaturday(new Date(startDateObj.getTime() + (weekIndex * 7 + dayIndex) * 24 * 60 * 60 * 1000))
                }
              });
            }
          });
        }
      });
    });

    // Update calendar events
    setEvents(prevEvents => {
      // Remove existing CSV events for this batch
      const filteredEvents = prevEvents.filter(event =>
        !event.extendedProps || event.extendedProps.type !== 'csv-schedule'
      );
      const newEvents = [...filteredEvents, ...calendarEvents];

      // Save to localStorage for persistence
      const batchCalendarData = JSON.parse(localStorage.getItem('batchCalendarData') || '{}');
      batchCalendarData[selectedBatchId] = {
        events: calendarEvents,
        startDate: startDate,
        generatedAt: new Date().toISOString()
      };
      localStorage.setItem('batchCalendarData', JSON.stringify(batchCalendarData));

      return newEvents;
    });
    
    const shiftedEvents = calendarEvents.filter(event => event.extendedProps.isShifted);
    const message = shiftedEvents.length > 0 
      ? `✅ Successfully populated calendar with ${calendarEvents.length} sessions starting from ${startDate}. ${shiftedEvents.length} sessions were shifted due to non-working Saturdays.`
      : `✅ Successfully populated calendar with ${calendarEvents.length} sessions starting from ${startDate}`;
    
    alert(message);
  };

  const handleEventClick = (clickInfo) => {
    if (!isScheduler) return;
    const d = clickInfo.event.extendedProps;

    if (d.type === 'batch-schedule') {
      // Open batch schedule edit modal for facilitator assignment
      // The entry data is spread into extendedProps, so d.id is the schedule entry id
      const scheduleEntry = batchScheduleData.find(entry => entry.id === d.id);
      if (scheduleEntry) {
        setSelectedScheduleEntry({
          ...scheduleEntry,
          eventTitle: clickInfo.event.title
        });
        setShowScheduleEditModal(true);
      } else {
        // Fallback: use the extendedProps directly if entry not found
        setSelectedScheduleEntry({
          ...d,
          eventTitle: clickInfo.event.title
        });
        setShowScheduleEditModal(true);
      }
      return;
    }

    setIsEditing(true);
    setFormData({ id: d.id, batchId: d.batchId, moduleId: d.moduleId, trainerId: d.trainerId, session_date: d.session_date, status: d.status });

    const selectedBatch = batches.find(b => b.id === d.batchId);
    setAvailableModules(selectedBatch?.program?.modules || []);

    setIsModalOpen(true);
  };

  const handleEventDrop = async (dropInfo) => {
    const { event, revert } = dropInfo;
    const props = event.extendedProps;

    // Only handle batch-schedule events
    if (props.type !== 'batch-schedule') {
      revert();
      return;
    }

    const entryId = props.id;
    const newDate = event.start;
    // Format as YYYY-MM-DD
    const newDateStr = newDate.getFullYear() + '-' +
      String(newDate.getMonth() + 1).padStart(2, '0') + '-' +
      String(newDate.getDate()).padStart(2, '0');

    try {
      await batchScheduleService.updateScheduleEntry(entryId, {
        session_date: newDateStr
      });
      // Reload schedule to reflect the persisted change
      if (selectedBatchId) {
        await fetchBatchSchedule(selectedBatchId);
      }
    } catch (error) {
      console.error('Error updating session date:', error);
      const errorMsg = error.response?.data?.message || 'Failed to move session. Please try again.';
      alert(errorMsg);
      revert();
    }
  };

  // --- MODAL FORM HANDLERS ---
  const handleCloseModal = () => setIsModalOpen(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleBatchChangeInModal = (e) => {
    const selectedBatchId = e.target.value;
    handleInputChange(e);
    if (selectedBatchId) {
      const selectedBatch = batches.find(b => b.id === parseInt(selectedBatchId));
      setAvailableModules(selectedBatch?.program?.modules || []);
    } else {
      setAvailableModules([]);
    }
    setFormData(prev => ({ ...prev, moduleId: '' }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const action = isEditing
      ? sessionService.updateSession(formData.id, formData)
      : sessionService.createSession(formData);
    action.then(() => {
      if (viewMode === 'batch-specific' && selectedBatchId) {
        fetchBatchSchedule(selectedBatchId);
      } else {
        fetchAllData();
      }
      handleCloseModal();
    }).catch(err => alert(`Error: ${err.response?.data?.message || 'Could not save session.'}`));
  };

  const handleDelete = () => {
    if (window.confirm('🗑️ Are you sure you want to delete this session? This action cannot be undone.')) {
      sessionService.deleteSession(formData.id).then(() => {
        if (viewMode === 'batch-specific' && selectedBatchId) {
          fetchBatchSchedule(selectedBatchId);
        } else {
          fetchAllData();
        }
        handleCloseModal();
        alert('✅ Session deleted successfully!');
      }).catch(err => {
        alert(`❌ Error deleting session: ${err.response?.data?.message || err.message}`);
      });
    }
  };

  // --- CUSTOM EVENT CONTENT RENDERER (shows facilitator on calendar) ---
  const renderEventContent = (eventInfo) => {
    const { event } = eventInfo;
    const props = event.extendedProps || {};

    // For batch schedule events, show facilitator name
    if (props.type === 'batch-schedule') {
      const trainerName = props.trainerName || (props.trainer?.name);
      return (
        <div className="custom-event-content">
          <div className="event-title">{event.title}</div>
          {trainerName && (
            <div className="event-facilitator">
              <span className="facilitator-icon">👨‍🏫</span>
              <span className="facilitator-text">{trainerName}</span>
            </div>
          )}
          {!trainerName && isScheduler && (
            <div className="event-facilitator unassigned">
              <span className="facilitator-text">Click to assign</span>
            </div>
          )}
        </div>
      );
    }

    // Default rendering for other event types
    return (
      <div className="custom-event-content">
        <div className="event-title">{event.title}</div>
      </div>
    );
  };

  // --- VIEW TAB DATA FETCHERS ---

  // Fetch trainer-wise data when tab is selected
  const fetchTrainerWiseData = useCallback(async () => {
    try {
      setLoading(true);
      const params = selectedBatchId ? { batchId: selectedBatchId } : {};
      const response = await batchScheduleService.getSchedulesByTrainer(params);
      if (response.data.success) {
        setTrainerWiseData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching trainer-wise data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedBatchId]);

  // Fetch center-wise data when tab is selected
  const fetchCenterWiseData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await batchScheduleService.getSchedulesByCenter({});
      if (response.data.success) {
        setCenterWiseData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching center-wise data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch zone-wise data when tab is selected
  const fetchZoneWiseData = useCallback(async () => {
    try {
      setLoading(true);
      // Compute week end (Saturday) from Monday start
      const parts = zoneWeekStart.split('-');
      const startDate = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + 5);
      const endStr = endDate.getFullYear() + '-' +
        String(endDate.getMonth() + 1).padStart(2, '0') + '-' +
        String(endDate.getDate()).padStart(2, '0');

      const [zoneResponse, taskResponse] = await Promise.all([
        batchScheduleService.getSchedulesByZone({
          weekStartDate: zoneWeekStart,
          includeCompleted: zoneFilters.includeCompleted,
          includeUpcoming: zoneFilters.includeUpcoming
        }),
        trainerTaskService.getTrainerTasks(zoneWeekStart, endStr)
      ]);
      if (zoneResponse.data.success) {
        setZoneWiseData(zoneResponse.data.data);
      }
      if (taskResponse.data.success) {
        setTrainerTasks(taskResponse.data.data);
      }
    } catch (error) {
      console.error('Error fetching zone-wise data:', error);
    } finally {
      setLoading(false);
    }
  }, [zoneWeekStart, zoneFilters.includeCompleted, zoneFilters.includeUpcoming]);

  // Handle view tab change
  const handleViewTabChange = (tab) => {
    setViewTab(tab);
    if (tab === 'trainer') {
      fetchTrainerWiseData();
    } else if (tab === 'center') {
      fetchCenterWiseData();
    } else if (tab === 'zone') {
      fetchZoneWiseData();
    } else if ((tab === 'calendar' || tab === 'table' || tab === 'week') && selectedBatchId) {
      fetchBatchSchedule(selectedBatchId);
    }
  };

  // Refetch zone data when week or batch-status filters change
  useEffect(() => {
    if (viewTab === 'zone') {
      fetchZoneWiseData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoneWeekStart, zoneFilters.includeCompleted, zoneFilters.includeUpcoming]);

  // Trainer task CRUD handlers
  const handleCreateTrainerTask = async (taskData) => {
    try {
      await trainerTaskService.createTrainerTask(taskData);
      await fetchZoneWiseData();
    } catch (error) {
      console.error('Error creating trainer task:', error);
      alert(error.response?.data?.message || 'Failed to create task');
    }
  };

  const handleUpdateTrainerTask = async (id, taskData) => {
    try {
      await trainerTaskService.updateTrainerTask(id, taskData);
      await fetchZoneWiseData();
    } catch (error) {
      console.error('Error updating trainer task:', error);
      alert(error.response?.data?.message || 'Failed to update task');
    }
  };

  const handleDeleteTrainerTask = async (id) => {
    try {
      await trainerTaskService.deleteTrainerTask(id);
      await fetchZoneWiseData();
    } catch (error) {
      console.error('Error deleting trainer task:', error);
      alert(error.response?.data?.message || 'Failed to delete task');
    }
  };

  // --- F1: INSERT-AND-AUTO-SHIFT HANDLERS ---

  const openInsertModal = (week, day) => {
    setInsertModal({ week, day });
    setInsertForm({ session_content: '', trainerId: '' });
    setInsertPreview(null);
    setInsertError('');
  };

  const closeInsertModal = () => {
    setInsertModal(null);
    setInsertPreview(null);
    setInsertError('');
    setInsertSubmitting(false);
  };

  // Preview how many sessions will shift and whether the batch overflows.
  const runInsertDryRun = async () => {
    if (!selectedBatchId || !insertModal) return;
    setInsertError('');
    try {
      const res = await batchScheduleService.insertSession(
        selectedBatchId,
        {
          week_number: insertModal.week,
          day_number: insertModal.day,
          session_content: insertForm.session_content || 'Placeholder',
          trainerId: insertForm.trainerId || null
        },
        { dryRun: true }
      );
      setInsertPreview(res.data.data || res.data);
    } catch (err) {
      if (err.response?.status === 409 && err.response?.data?.needsExtension) {
        setInsertPreview(err.response.data);
      } else {
        setInsertError(err.response?.data?.message || err.message || 'Preview failed');
      }
    }
  };

  // Run the preview automatically whenever the user opens the modal or changes
  // the target slot — keeps the "X sessions will shift" count fresh.
  useEffect(() => {
    if (insertModal && selectedBatchId) {
      runInsertDryRun();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insertModal, selectedBatchId]);

  const submitInsert = async (confirmExtend = false) => {
    if (!selectedBatchId || !insertModal) return;
    if (!insertForm.session_content.trim()) {
      setInsertError('Session content is required.');
      return;
    }
    setInsertError('');
    setInsertSubmitting(true);
    try {
      await batchScheduleService.insertSession(selectedBatchId, {
        week_number: insertModal.week,
        day_number: insertModal.day,
        session_content: insertForm.session_content.trim(),
        trainerId: insertForm.trainerId || null,
        confirmExtend
      });
      closeInsertModal();
      await fetchBatchSchedule(selectedBatchId);
    } catch (err) {
      if (err.response?.status === 409 && err.response?.data?.needsExtension) {
        setInsertPreview(err.response.data);
      } else {
        setInsertError(err.response?.data?.message || err.message || 'Insert failed');
      }
    } finally {
      setInsertSubmitting(false);
    }
  };

  // --- VIEW RENDERING FUNCTIONS ---

  // Render trainer-wise view
  const renderTrainerWiseView = () => {
    if (!trainerWiseData) {
      return (
        <div className="loading-spinner">
          <div className="spinner"></div>
          <span>Loading trainer data...</span>
        </div>
      );
    }

    const { byTrainer, unassigned } = trainerWiseData;

    return (
      <div className="trainer-wise-view">
        {byTrainer.map(({ trainer, sessions }) => (
          <div key={trainer.id} className="trainer-card">
            <div className="trainer-card-header">
              <h3>👨‍🏫 {trainer.name}</h3>
              <span className="session-count">{sessions.length} sessions</span>
            </div>
            <div className="trainer-sessions">
              <table className="trainer-sessions-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Timing</th>
                    <th>Location</th>
                    <th>Batch</th>
                    <th>Session</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map(session => (
                    <tr key={session.id}>
                      <td>
                        {session.session_date
                          ? parseLocalDate(session.session_date)?.toLocaleDateString('en-IN', {
                              weekday: 'short',
                              day: 'numeric',
                              month: 'short'
                            })
                          : '-'}
                      </td>
                      <td className="timing-cell">
                        {session.batch?.start_time && session.batch?.end_time
                          ? `${session.batch.start_time} - ${session.batch.end_time}`
                          : session.batch?.batch_type
                            ? session.batch.batch_type === 'morning' ? '10:00 AM - 1:00 PM' : '2:00 PM - 5:00 PM'
                            : '-'}
                      </td>
                      <td>{session.batch?.center?.name || '-'}</td>
                      <td>{session.batch?.batch_name || '-'}</td>
                      <td>{session.session_content}</td>
                      <td>
                        <span className={`status-badge status-${session.status?.toLowerCase() || 'scheduled'}`}>
                          {session.status || 'Scheduled'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}

        {unassigned.length > 0 && (
          <div className="trainer-card unassigned">
            <div className="trainer-card-header">
              <h3>⚠️ Unassigned Sessions</h3>
              <span className="session-count">{unassigned.length} sessions</span>
            </div>
            <div className="trainer-sessions">
              <table className="trainer-sessions-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Timing</th>
                    <th>Location</th>
                    <th>Batch</th>
                    <th>Session</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {unassigned.map(session => (
                    <tr key={session.id}>
                      <td>
                        {session.session_date
                          ? parseLocalDate(session.session_date)?.toLocaleDateString('en-IN', {
                              weekday: 'short',
                              day: 'numeric',
                              month: 'short'
                            })
                          : '-'}
                      </td>
                      <td className="timing-cell">
                        {session.batch?.start_time && session.batch?.end_time
                          ? `${session.batch.start_time} - ${session.batch.end_time}`
                          : session.batch?.batch_type
                            ? session.batch.batch_type === 'morning' ? '10:00 AM - 1:00 PM' : '2:00 PM - 5:00 PM'
                            : '-'}
                      </td>
                      <td>{session.batch?.center?.name || '-'}</td>
                      <td>{session.batch?.batch_name || '-'}</td>
                      <td>{session.session_content}</td>
                      <td>
                        <span className={`status-badge status-${session.status?.toLowerCase() || 'scheduled'}`}>
                          {session.status || 'Scheduled'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render center-wise view
  const renderCenterWiseView = () => {
    if (!centerWiseData) {
      return (
        <div className="loading-spinner">
          <div className="spinner"></div>
          <span>Loading center data...</span>
        </div>
      );
    }

    // Find max sessions across all batches for row alignment
    const getMaxSessions = (batches) => {
      return Math.max(...batches.map(b => b.sessions.length), 0);
    };

    return (
      <div className="center-wise-view">
        {centerWiseData.map(({ center, batches }) => {
          const morningBatches = batches.filter(b => b.batch.batch_type !== 'afternoon');
          const afternoonBatches = batches.filter(b => b.batch.batch_type === 'afternoon');
          const totalSessions = batches.reduce((sum, b) => sum + b.sessions.length, 0);
          const maxSessions = getMaxSessions(batches);

          return (
            <div key={center.id} className="center-card">
              <div className="center-card-header">
                <div className="center-header-info">
                  <h3>🏢 {center.name}</h3>
                  {center.address && <span className="center-address">{center.address}</span>}
                </div>
                <div className="center-stats">
                  <span className="stat-item">📦 {batches.length} batches</span>
                  <span className="stat-item">🌅 {morningBatches.length} AM</span>
                  <span className="stat-item">🌤️ {afternoonBatches.length} PM</span>
                  <span className="stat-item">📅 {totalSessions} sessions</span>
                </div>
              </div>

              {/* Horizontal Batch Comparison Table */}
              <div className="batch-comparison-container">
                <table className="batch-comparison-table">
                  <thead>
                    {/* Batch Name Header Row */}
                    <tr className="batch-header-row">
                      {batches.map(({ batch }) => (
                        <th key={batch.id} colSpan="4" className="batch-name-header">
                          <div className="batch-header-content">
                            <span className="batch-title">{batch.batch_name}</span>
                            <span className={`batch-timing-tag ${batch.batch_type || 'morning'}`}>
                              {batch.batch_type === 'custom'
                                ? `${batch.start_time || ''} - ${batch.end_time || ''}`
                                : batch.batch_type === 'afternoon' ? 'PM' : 'AM'}
                            </span>
                            <span className={`batch-status-tag ${batch.status?.toLowerCase() || 'upcoming'}`}>
                              {batch.status || 'Upcoming'}
                            </span>
                          </div>
                        </th>
                      ))}
                    </tr>
                    {/* Column Headers Row */}
                    <tr className="column-header-row">
                      {batches.map(({ batch }) => (
                        <React.Fragment key={batch.id}>
                          <th>Week</th>
                          <th>Date</th>
                          <th>Sessions</th>
                          <th>Faculty</th>
                        </React.Fragment>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Data Rows - aligned by session index */}
                    {Array.from({ length: maxSessions }).map((_, rowIndex) => (
                      <tr key={rowIndex}>
                        {batches.map(({ batch, sessions }) => {
                          const session = sessions[rowIndex];
                          if (!session) {
                            return (
                              <React.Fragment key={batch.id}>
                                <td className="empty-cell">-</td>
                                <td className="empty-cell">-</td>
                                <td className="empty-cell">-</td>
                                <td className="empty-cell">-</td>
                              </React.Fragment>
                            );
                          }
                          return (
                            <React.Fragment key={batch.id}>
                              <td className="week-cell">Week {session.week_number}</td>
                              <td className="date-cell">
                                {session.session_date
                                  ? parseLocalDate(session.session_date)?.toLocaleDateString('en-IN', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: 'numeric'
                                    })
                                  : '-'}
                              </td>
                              <td className="session-cell">{session.session_content || '-'}</td>
                              <td className="faculty-cell">
                                {isScheduler ? (
                                  <>
                                    <select
                                      value={session.trainerId || ''}
                                      onChange={(e) => handleCenterFacilitatorChange(session.id, e.target.value)}
                                      disabled={savingSession === session.id}
                                      className={`facilitator-select ${savingSession === session.id ? 'saving' : ''}`}
                                    >
                                      <option value="">-- Assign --</option>
                                      {trainers.map(trainer => (
                                        <option key={trainer.id} value={trainer.id}>
                                          {trainer.name}
                                        </option>
                                      ))}
                                    </select>
                                  </>
                                ) : (
                                  <span className="facilitator-name">
                                    {session.trainer?.name || '-'}
                                  </span>
                                )}
                              </td>
                            </React.Fragment>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Legacy zone-wise view (replaced by ZoneWeeklyView component)
  // eslint-disable-next-line no-unused-vars
  const renderZoneWiseView = () => {
    if (!zoneWiseData) {
      return (
        <div className="loading-spinner">
          <div className="spinner"></div>
          <span>Loading zone data...</span>
        </div>
      );
    }

    if (zoneWiseData.length === 0) {
      return (
        <div className="empty-table-message">
          <p>📍 No schedule data available. Upload schedules to see zone-wise view.</p>
        </div>
      );
    }

    // Zone colors for visual distinction
    const zoneColors = {
      'North Delhi': '#ef4444',
      'South Delhi': '#10b981',
      'East Delhi': '#f59e0b',
      'West Delhi': '#6366f1',
      'Noida': '#8b5cf6',
      'Gurugram': '#06b6d4',
      'Unassigned': '#6b7280'
    };

    // Find max sessions across batches for row alignment
    const getMaxSessions = (batches) => {
      return Math.max(...batches.map(b => b.sessions.length), 0);
    };

    return (
      <div className="zone-wise-view">
        {zoneWiseData.map(({ zone, centers }) => {
          const totalSessions = centers.reduce((sum, c) =>
            sum + c.batches.reduce((bSum, b) => bSum + b.sessions.length, 0), 0
          );
          const totalBatches = centers.reduce((sum, c) => sum + c.batches.length, 0);
          const morningBatches = centers.reduce((sum, c) =>
            sum + c.batches.filter(b => b.batch.batch_type !== 'afternoon').length, 0
          );
          const afternoonBatches = totalBatches - morningBatches;

          return (
            <div key={zone} className="zone-card">
              <div
                className="zone-card-header"
                style={{ background: `linear-gradient(135deg, ${zoneColors[zone] || '#6b7280'} 0%, ${zoneColors[zone] || '#6b7280'}dd 100%)` }}
              >
                <h3>📍 {zone}</h3>
                <div className="zone-stats">
                  <span>🏢 {centers.length} centers</span>
                  <span>📦 {totalBatches} batches</span>
                  <span>🌅 {morningBatches} AM</span>
                  <span>🌤️ {afternoonBatches} PM</span>
                  <span>📅 {totalSessions} sessions</span>
                </div>
              </div>
              <div className="zone-card-body">
                {centers.map(({ center, batches }) => {
                  const centerTotalSessions = batches.reduce((sum, b) => sum + b.sessions.length, 0);
                  const maxSessions = getMaxSessions(batches);

                  return (
                    <div key={center.id} className="zone-center-item">
                      <div className="zone-center-header">
                        <div className="zone-center-info">
                          <strong>🏢 {center.name}</strong>
                          {center.address && <span className="center-address">{center.address}</span>}
                        </div>
                        <div className="zone-center-stats">
                          <span>{batches.length} batches</span>
                          <span>{centerTotalSessions} sessions</span>
                        </div>
                      </div>

                      {/* Horizontal Batch Comparison Table */}
                      <div className="batch-comparison-container">
                        <table className="batch-comparison-table">
                          <thead>
                            {/* Batch Name Header Row */}
                            <tr className="batch-header-row">
                              {batches.map(({ batch }) => (
                                <th key={batch.id} colSpan="4" className="batch-name-header">
                                  <div className="batch-header-content">
                                    <span className="batch-title">{batch.batch_name}</span>
                                    <span className={`batch-timing-tag ${batch.batch_type || 'morning'}`}>
                                      {batch.batch_type === 'custom'
                                        ? `${batch.start_time || ''} - ${batch.end_time || ''}`
                                        : batch.batch_type === 'afternoon' ? 'PM' : 'AM'}
                                    </span>
                                    <span className={`batch-status-tag ${batch.status?.toLowerCase() || 'upcoming'}`}>
                                      {batch.status || 'Upcoming'}
                                    </span>
                                  </div>
                                </th>
                              ))}
                            </tr>
                            {/* Column Headers Row */}
                            <tr className="column-header-row">
                              {batches.map(({ batch }) => (
                                <React.Fragment key={batch.id}>
                                  <th>Week</th>
                                  <th>Date</th>
                                  <th>Sessions</th>
                                  <th>Faculty</th>
                                </React.Fragment>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {/* Data Rows - aligned by session index */}
                            {Array.from({ length: maxSessions }).map((_, rowIndex) => (
                              <tr key={rowIndex}>
                                {batches.map(({ batch, sessions }) => {
                                  const session = sessions[rowIndex];
                                  if (!session) {
                                    return (
                                      <React.Fragment key={batch.id}>
                                        <td className="empty-cell">-</td>
                                        <td className="empty-cell">-</td>
                                        <td className="empty-cell">-</td>
                                        <td className="empty-cell">-</td>
                                      </React.Fragment>
                                    );
                                  }
                                  return (
                                    <React.Fragment key={batch.id}>
                                      <td className="week-cell">Week {session.week_number}</td>
                                      <td className="date-cell">
                                        {session.session_date
                                          ? parseLocalDate(session.session_date)?.toLocaleDateString('en-IN', {
                                              day: '2-digit',
                                              month: '2-digit',
                                              year: 'numeric'
                                            })
                                          : '-'}
                                      </td>
                                      <td className="session-cell">{session.session_content || '-'}</td>
                                      <td className="faculty-cell">
                                        {isScheduler ? (
                                          <>
                                            <select
                                              value={session.trainerId || ''}
                                              onChange={(e) => handleZoneFacilitatorChange(session.id, e.target.value)}
                                              disabled={savingSession === session.id}
                                              className={`facilitator-select ${savingSession === session.id ? 'saving' : ''}`}
                                            >
                                              <option value="">-- Assign --</option>
                                              {trainers.map(trainer => (
                                                <option key={trainer.id} value={trainer.id}>
                                                  {trainer.name}
                                                </option>
                                              ))}
                                            </select>
                                          </>
                                        ) : (
                                          <span className="facilitator-name">
                                            {session.trainer?.name || '-'}
                                          </span>
                                        )}
                                      </td>
                                    </React.Fragment>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Render week-wise view (grouped across batches)
  const renderWeekWiseView = () => {
    if (!batchScheduleData || batchScheduleData.length === 0) {
      return (
        <div className="empty-table-message">
          <p>📊 No schedule data available. Please select a batch or upload schedule data.</p>
        </div>
      );
    }

    // Group by week
    const weekGroups = {};
    batchScheduleData.forEach(entry => {
      const weekKey = entry.week_number;
      if (!weekGroups[weekKey]) {
        weekGroups[weekKey] = [];
      }
      weekGroups[weekKey].push(entry);
    });

    return (
      <div className="week-wise-view">
        {Object.keys(weekGroups)
          .sort((a, b) => parseInt(a) - parseInt(b))
          .map(weekNum => {
            const sessions = weekGroups[weekNum];
            const completedCount = sessions.filter(s => s.status === 'completed').length;

            return (
              <div key={weekNum} className="week-block">
                <div className="week-block-header">
                  <h3>📅 Week {weekNum}</h3>
                  <div className="week-stats">
                    <span>{sessions.length} sessions</span>
                    <span className="completed">{completedCount} completed</span>
                  </div>
                </div>
                <table className="week-block-table">
                  <thead>
                    <tr>
                      <th>Day</th>
                      <th>Date</th>
                      <th>Session</th>
                      <th>Facilitator</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessions
                      .sort((a, b) => a.day_number - b.day_number)
                      .map(session => (
                        <tr key={session.id}>
                          <td>Day {session.day_number}</td>
                          <td>
                            {session.session_date
                              ? parseLocalDate(session.session_date)?.toLocaleDateString('en-IN', {
                                  weekday: 'short',
                                  day: 'numeric',
                                  month: 'short'
                                })
                              : '-'}
                          </td>
                          <td>{session.session_content}</td>
                          <td>{session.trainer?.name || '-'}</td>
                          <td>
                            <span className={`status-badge status-${session.status?.toLowerCase() || 'scheduled'}`}>
                              {session.status || 'Scheduled'}
                            </span>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            );
          })}
      </div>
    );
  };

  // --- EXPORT FUNCTIONS ---

  // Export current batch schedule to Excel
  const exportScheduleToExcel = () => {
    if (!batchScheduleData || batchScheduleData.length === 0) {
      alert('No schedule data to export. Please select a batch first.');
      return;
    }

    const selectedBatch = batches.find(b => b.id === parseInt(selectedBatchId));
    const batchName = selectedBatch?.batch_name || 'Schedule';

    // Prepare data for export
    const exportData = batchScheduleData.map(entry => ({
      'Week': `Week ${entry.week_number}`,
      'Day': `Day ${entry.day_number}`,
      'Date': entry.session_date ? parseLocalDate(entry.session_date)?.toLocaleDateString('en-IN', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      }) || '-' : '-',
      'Session': entry.session_content,
      'Facilitator': entry.trainer?.name || '-',
      'Status': entry.status || 'Scheduled'
    }));

    // Create workbook and worksheet
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Schedule');

    // Auto-size columns
    const colWidths = [
      { wch: 10 }, // Week
      { wch: 8 },  // Day
      { wch: 18 }, // Date
      { wch: 40 }, // Session
      { wch: 20 }, // Facilitator
      { wch: 12 }  // Status
    ];
    ws['!cols'] = colWidths;

    // Save file
    XLSX.writeFile(wb, `${batchName}_Schedule.xlsx`);
  };

  // Export trainer-wise schedule
  const exportTrainerWiseSchedule = () => {
    if (!batchScheduleData || batchScheduleData.length === 0) {
      alert('No schedule data to export.');
      return;
    }

    const selectedBatch = batches.find(b => b.id === parseInt(selectedBatchId));

    // Helper to get timing string
    const getTiming = (batch) => {
      if (batch?.start_time && batch?.end_time) {
        return `${batch.start_time} - ${batch.end_time}`;
      }
      if (batch?.batch_type) {
        return batch.batch_type === 'morning' ? '10:00 AM - 1:00 PM' : '2:00 PM - 5:00 PM';
      }
      return '-';
    };

    // Group by trainer
    const trainerGroups = {};
    batchScheduleData.forEach(entry => {
      const trainerName = entry.trainer?.name || 'Unassigned';
      if (!trainerGroups[trainerName]) {
        trainerGroups[trainerName] = [];
      }
      trainerGroups[trainerName].push(entry);
    });

    // Prepare data for export
    const exportData = [];
    Object.keys(trainerGroups).sort().forEach(trainerName => {
      trainerGroups[trainerName].forEach(entry => {
        exportData.push({
          'Facilitator': trainerName,
          'Date': entry.session_date ? parseLocalDate(entry.session_date)?.toLocaleDateString('en-IN', {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
            year: 'numeric'
          }) || '-' : '-',
          'Timing': getTiming(selectedBatch),
          'Location': selectedBatch?.center?.name || '-',
          'Batch': selectedBatch?.batch_name || '-',
          'Week': `Week ${entry.week_number}`,
          'Day': `Day ${entry.day_number}`,
          'Session': entry.session_content,
          'Status': entry.status || 'Scheduled'
        });
      });
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Trainer Schedule');

    ws['!cols'] = [
      { wch: 20 }, { wch: 18 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 10 }, { wch: 8 }, { wch: 40 }, { wch: 12 }
    ];

    XLSX.writeFile(wb, `${selectedBatch?.batch_name || 'Schedule'}_TrainerWise.xlsx`);
  };

  // Export week-wise schedule (cross-batch if viewing all)
  const exportWeekWiseSchedule = () => {
    if (!batchScheduleData || batchScheduleData.length === 0) {
      alert('No schedule data to export.');
      return;
    }

    // Group by week
    const weekGroups = {};
    batchScheduleData.forEach(entry => {
      const weekKey = `Week ${entry.week_number}`;
      if (!weekGroups[weekKey]) {
        weekGroups[weekKey] = [];
      }
      weekGroups[weekKey].push(entry);
    });

    // Prepare data for export
    const exportData = [];
    Object.keys(weekGroups).sort((a, b) => {
      return parseInt(a.replace('Week ', '')) - parseInt(b.replace('Week ', ''));
    }).forEach(weekKey => {
      weekGroups[weekKey].forEach(entry => {
        exportData.push({
          'Week': weekKey,
          'Day': `Day ${entry.day_number}`,
          'Date': entry.session_date ? parseLocalDate(entry.session_date)?.toLocaleDateString('en-IN') || '-' : '-',
          'Session': entry.session_content,
          'Facilitator': entry.trainer?.name || '-',
          'Status': entry.status || 'Scheduled'
        });
      });
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Week Schedule');

    ws['!cols'] = [
      { wch: 10 }, { wch: 8 }, { wch: 15 }, { wch: 40 }, { wch: 20 }, { wch: 12 }
    ];

    const selectedBatch = batches.find(b => b.id === parseInt(selectedBatchId));
    XLSX.writeFile(wb, `${selectedBatch?.batch_name || 'Schedule'}_WeekWise.xlsx`);
  };

  // Export center-wise schedule
  const exportCenterWiseSchedule = () => {
    if (!centerWiseData || centerWiseData.length === 0) {
      alert('No center data to export.');
      return;
    }

    // Helper to get timing string
    const getTiming = (batch) => {
      if (batch?.start_time && batch?.end_time) {
        return `${batch.start_time} - ${batch.end_time}`;
      }
      return batch?.batch_type === 'afternoon' ? '2:00 PM - 5:00 PM' : '10:00 AM - 1:00 PM';
    };

    // Helper to get unique facilitators
    const getUniqueFacilitators = (sessions) => {
      const facilitators = sessions
        .filter(s => s.trainer?.name)
        .map(s => s.trainer.name);
      return [...new Set(facilitators)].join(', ') || 'Not assigned';
    };

    // Prepare batch overview data
    const exportData = [];
    centerWiseData.forEach(({ center, batches }) => {
      batches.forEach(({ batch, sessions }) => {
        const assignedSessions = sessions.filter(s => s.trainer?.name).length;
        exportData.push({
          'Center': center.name,
          'Address': center.address || '-',
          'Batch Name': batch.batch_name,
          'Status': batch.status || 'Upcoming',
          'Timing': getTiming(batch),
          'Start Date': batch.start_date ? parseLocalDate(batch.start_date)?.toLocaleDateString('en-IN') || '-' : '-',
          'End Date': batch.end_date ? parseLocalDate(batch.end_date)?.toLocaleDateString('en-IN') || '-' : '-',
          'Faculty Allotted': getUniqueFacilitators(sessions),
          'Total Sessions': sessions.length,
          'Assigned Sessions': assignedSessions,
          'Unassigned Sessions': sessions.length - assignedSessions
        });
      });
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Center Schedule');

    ws['!cols'] = [
      { wch: 25 }, { wch: 30 }, { wch: 20 }, { wch: 12 }, { wch: 20 },
      { wch: 12 }, { wch: 12 }, { wch: 35 }, { wch: 12 }, { wch: 15 }, { wch: 18 }
    ];

    XLSX.writeFile(wb, 'CenterWise_Schedule.xlsx');
  };

  // Export zone-wise schedule
  const exportZoneWiseSchedule = () => {
    if (!zoneWiseData || zoneWiseData.length === 0) {
      alert('No zone data to export.');
      return;
    }

    // Helper to get timing string
    const getTiming = (batch) => {
      if (batch?.start_time && batch?.end_time) {
        return `${batch.start_time} - ${batch.end_time}`;
      }
      return batch?.batch_type === 'afternoon' ? '2:00 PM - 5:00 PM' : '10:00 AM - 1:00 PM';
    };

    // Helper to get unique facilitators
    const getUniqueFacilitators = (sessions) => {
      const facilitators = sessions
        .filter(s => s.trainer?.name)
        .map(s => s.trainer.name);
      return [...new Set(facilitators)].join(', ') || 'Not assigned';
    };

    // Prepare batch overview data grouped by zone
    const exportData = [];
    zoneWiseData.forEach(({ zone, centers }) => {
      centers.forEach(({ center, batches }) => {
        batches.forEach(({ batch, sessions }) => {
          const assignedSessions = sessions.filter(s => s.trainer?.name).length;
          exportData.push({
            'Zone': zone,
            'Center': center.name,
            'Address': center.address || '-',
            'Batch Name': batch.batch_name,
            'Status': batch.status || 'Upcoming',
            'Timing': getTiming(batch),
            'Start Date': batch.start_date ? parseLocalDate(batch.start_date)?.toLocaleDateString('en-IN') || '-' : '-',
            'End Date': batch.end_date ? parseLocalDate(batch.end_date)?.toLocaleDateString('en-IN') || '-' : '-',
            'Faculty Allotted': getUniqueFacilitators(sessions),
            'Total Sessions': sessions.length,
            'Assigned Sessions': assignedSessions,
            'Unassigned Sessions': sessions.length - assignedSessions
          });
        });
      });
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Zone Schedule');

    ws['!cols'] = [
      { wch: 15 }, { wch: 25 }, { wch: 30 }, { wch: 20 }, { wch: 12 },
      { wch: 20 }, { wch: 12 }, { wch: 12 }, { wch: 35 }, { wch: 12 }, { wch: 15 }, { wch: 18 }
    ];

    XLSX.writeFile(wb, 'ZoneWise_Schedule.xlsx');
  };

  // Print zone weekly view as PDF (uses browser's print-to-PDF)
  const printZoneWeeklyView = () => {
    if (!zoneWiseData || zoneWiseData.length === 0) {
      alert('No zone data to print.');
      return;
    }
    document.body.classList.add('print-zone-view');
    const cleanup = () => {
      document.body.classList.remove('print-zone-view');
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);
    window.print();
  };

  // --- STATUS LEGEND COMPONENT ---
  const renderStatusLegend = () => (
    <div className="status-legend">
      <span className="legend-title">Status:</span>
      <div className="legend-items">
        <span className="legend-item">
          <span className="legend-dot scheduled"></span>
          Scheduled
        </span>
        <span className="legend-item">
          <span className="legend-dot completed"></span>
          Completed
        </span>
        <span className="legend-item">
          <span className="legend-dot cancelled"></span>
          Cancelled
        </span>
        <span className="legend-item">
          <span className="legend-dot rescheduled"></span>
          Rescheduled
        </span>
      </div>
    </div>
  );

  // --- DASHBOARD STATS COMPONENT ---
  const renderDashboardStats = () => {
    const totalEvents = events.length;
    const publishedEvents = events.filter(e => e.extendedProps?.status === 'Published').length;
    const completedEvents = events.filter(e => e.extendedProps?.status === 'Completed' || e.extendedProps?.status === 'completed').length;
    const draftEvents = events.filter(e => e.extendedProps?.status === 'Draft').length;
    const cancelledEvents = events.filter(e => e.extendedProps?.status === 'Cancelled' || e.extendedProps?.status === 'cancelled').length;
    const scheduledEvents = events.filter(e => e.extendedProps?.status === 'scheduled').length;

    const completionRate = totalEvents > 0 ? Math.round((completedEvents / totalEvents) * 100) : 0;

    const selectedBatch = selectedBatchId ? batches.find(b => b.id === parseInt(selectedBatchId)) : null;

    return (
      <div className="dashboard-stats fade-in">
        <div className="stat-card primary">
          <div className="stat-card-content">
            <span className="stat-card-title">Total Sessions:</span>
            <span className="stat-card-value">{totalEvents}</span>
          </div>
          <div className="stat-card-subtitle">
            {selectedBatch ? `${selectedBatch.batch_name}` : 'All batches'}
          </div>
        </div>

        {/* Progress Bar Card */}
        {batchProgress && (
          <div className="stat-card progress-card">
            <div className="stat-card-content">
              <span className="stat-card-title">Progress:</span>
              <span className="stat-card-value">{batchProgress.progress.completionPercentage}%</span>
            </div>
            <div className="progress-bar-container">
              <div
                className="progress-bar-fill"
                style={{ width: `${batchProgress.progress.completionPercentage}%` }}
              ></div>
            </div>
            <div className="stat-card-subtitle">
              {batchProgress.progress.completedSessions} / {batchProgress.progress.totalSessions} sessions
            </div>
          </div>
        )}

        <div className="stat-card success">
          <div className="stat-card-content">
            <span className="stat-card-title">Completed:</span>
            <span className="stat-card-value">{completedEvents}</span>
          </div>
          <div className="stat-card-subtitle">
            {completionRate}% completion rate
          </div>
        </div>

        {scheduledEvents > 0 && (
          <div className="stat-card info">
            <div className="stat-card-content">
              <span className="stat-card-title">Scheduled:</span>
              <span className="stat-card-value">{scheduledEvents}</span>
            </div>
            <div className="stat-card-subtitle">
              Upcoming sessions
            </div>
          </div>
        )}

        <div className="stat-card warning">
          <div className="stat-card-content">
            <span className="stat-card-title">Published:</span>
            <span className="stat-card-value">{publishedEvents}</span>
          </div>
          <div className="stat-card-subtitle">
            Ready for delivery
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-content">
            <span className="stat-card-title">Draft:</span>
            <span className="stat-card-value">{draftEvents}</span>
          </div>
          <div className="stat-card-subtitle">
            Pending review
          </div>
        </div>

        {cancelledEvents > 0 && (
          <div className="stat-card danger">
            <div className="stat-card-content">
              <span className="stat-card-title">Cancelled:</span>
              <span className="stat-card-value">{cancelledEvents}</span>
            </div>
            <div className="stat-card-subtitle">
              Requires attention
            </div>
          </div>
        )}
      </div>
    );
  };

  // Custom CSV parser that handles Excel multi-line cells like Gemini
  const parseExcelCsvWithContinuationRows = (csvText) => {
    // Use Papa Parse first to handle CSV structure properly
    const parsed = Papa.parse(csvText, {
      skipEmptyLines: false,
      quoteChar: '"',
      delimiter: ','
    });

    const processedData = [];
    let currentRow = null;
    let headers = null;

    for (let i = 0; i < parsed.data.length; i++) {
      const row = parsed.data[i];
      if (!row || row.length === 0) continue;

      // Skip title rows like "New Framework" - look for Week headers
      const hasWeekHeaders = row.some(cell =>
        cell && cell.toString().toLowerCase().includes('week')
      );

      // Find the header row with Week columns
      if (!headers && hasWeekHeaders) {
        headers = row.map(h => (h || '').trim());
        continue;
      }

      // Skip rows that don't have headers yet
      if (!headers) {
        continue;
      }

      // Check if this row starts a new day
      const firstCol = (row[0] || '').trim();

      if (firstCol.match(/^Day\s+\d+/i)) {
        // Save previous row if exists
        if (currentRow) {
          processedData.push(currentRow);
        }

        // Start new row
        currentRow = {};
        // eslint-disable-next-line no-loop-func
        headers.forEach((header, index) => {
          currentRow[header] = (row[index] || '').trim();
        });

      } else if (currentRow && row.some(cell => cell && cell.trim())) {
        // This is a continuation row - merge content with <br> tags
        // eslint-disable-next-line no-loop-func
        headers.forEach((header, index) => {
          const newContent = (row[index] || '').trim();
          if (newContent) {
            if (currentRow[header] && currentRow[header].trim()) {
              currentRow[header] += '<br>' + newContent;
            } else {
              currentRow[header] = newContent;
            }
          }
        });
      }
    }

    // Don't forget the last row
    if (currentRow) {
      processedData.push(currentRow);
    }

    return processedData;
  };

  // --- ENHANCED TABLE VIEW WITH FACILITATOR ASSIGNMENT ---
  const renderCsvTableView = () => {
    // Use database schedule data if available, otherwise fall back to CSV data
    if (batchScheduleData && batchScheduleData.length > 0) {
      // Group schedule data by week
      const groupedByWeek = {};
      batchScheduleData.forEach(entry => {
        const weekKey = `Week ${entry.week_number}`;
        if (!groupedByWeek[weekKey]) {
          groupedByWeek[weekKey] = [];
        }
        groupedByWeek[weekKey].push(entry);
      });

      // Sort each week's entries by day
      Object.keys(groupedByWeek).forEach(week => {
        groupedByWeek[week].sort((a, b) => a.day_number - b.day_number);
      });

      const dayLabels = ['Day 1', 'Day 2', 'Day 3', 'Day 4', 'Day 5', 'Day 6'];

      // Helper to format date nicely (timezone-safe: parses YYYY-MM-DD as local date)
      const formatSessionDate = (dateStr) => {
        if (!dateStr) return '';
        const parts = String(dateStr).split('T')[0].split('-');
        const date = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
        if (isNaN(date.getTime())) return '';
        return date.toLocaleDateString('en-IN', {
          weekday: 'short',
          day: 'numeric',
          month: 'short'
        });
      };

      return (
        <div className="schedule-grid">
          {Object.keys(groupedByWeek).sort((a, b) => {
            const weekA = parseInt(a.replace('Week ', ''));
            const weekB = parseInt(b.replace('Week ', ''));
            return weekA - weekB;
          }).map(weekKey => (
            <div key={weekKey} className="week-card">
              <div className="week-card-header">
                <h3>{weekKey}</h3>
              </div>
              <div className="week-card-body">
                <table className="week-table">
                  <thead>
                    <tr>
                      <th>Day</th>
                      <th>Session</th>
                      <th className="facilitator-cell">Facilitator</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dayLabels.map((dayLabel, dayIndex) => {
                      const dayNum = dayIndex + 1;
                      const daySessions = groupedByWeek[weekKey].filter(s => s.day_number === dayNum);
                      const weekNumForInsert = parseInt(String(weekKey).replace('Week ', ''), 10);

                      if (daySessions.length === 0) {
                        return (
                          <tr key={dayNum} className="empty-day">
                            <td>{dayLabel}</td>
                            <td className="no-session">
                              {isScheduler ? (
                                <button
                                  type="button"
                                  className="insert-session-btn"
                                  onClick={() => openInsertModal(weekNumForInsert, dayNum)}
                                  title="Insert a session here"
                                >+ Insert session</button>
                              ) : '-'}
                            </td>
                            <td>-</td>
                            <td>-</td>
                          </tr>
                        );
                      }

                      // Get the date from the first session of this day
                      const sessionDate = daySessions[0]?.session_date;
                      const formattedDate = formatSessionDate(sessionDate);

                      return daySessions.map((session, idx) => {
                        const availableFacilitators = getAvailableFacilitators(session.session_date, session.id);
                        const isSaving = savingSession === session.id;

                        return (
                          <tr key={`${dayNum}-${idx}`}>
                            {idx === 0 && (
                              <td rowSpan={daySessions.length} className="day-cell">
                                <div className="day-label">{dayLabel}</div>
                                {formattedDate && (
                                  <div className="day-date">{formattedDate}</div>
                                )}
                                {isScheduler && (
                                  <button
                                    type="button"
                                    className="insert-session-btn insert-above-btn"
                                    onClick={() => openInsertModal(weekNumForInsert, dayNum)}
                                    title="Insert a session before this day and shift the rest down"
                                  >+ Insert above</button>
                                )}
                              </td>
                            )}
                            <td className="session-content">{session.session_content}</td>
                            <td className="facilitator-cell">
                              {isScheduler ? (
                                <>
                                  <select
                                    value={session.trainerId || ''}
                                    onChange={(e) => handleFacilitatorChange(session.id, e.target.value)}
                                    disabled={isSaving}
                                    className={`facilitator-select ${isSaving ? 'saving' : ''}`}
                                  >
                                    <option value="">-- Assign --</option>
                                    {/* Show currently assigned trainer even if they're assigned elsewhere */}
                                    {session.trainerId && !availableFacilitators.find(t => t.id === session.trainerId) && (
                                      <option value={session.trainerId}>
                                        {session.trainer?.name || 'Assigned Trainer'}
                                      </option>
                                    )}
                                    {availableFacilitators.map(trainer => (
                                      <option key={trainer.id} value={trainer.id}>
                                        {trainer.name}
                                      </option>
                                    ))}
                                  </select>
                                  {availableFacilitators.length < trainers.length && (() => {
                                    const leaveNames = getTrainersOnLeaveNames(session.session_date);
                                    const unavailableCount = trainers.length - availableFacilitators.length;
                                    return (
                                      <small className="conflict-hint">
                                        {unavailableCount} unavailable
                                        {leaveNames.length > 0 &&
                                          ` (on leave: ${leaveNames.join(', ')})`}
                                      </small>
                                    );
                                  })()}
                                </>
                              ) : (
                                <span className="facilitator-name">
                                  {session.trainer?.name || '-'}
                                </span>
                              )}
                            </td>
                            <td>
                              <span className={`status-badge status-${session.status?.toLowerCase() || 'scheduled'}`}>
                                {session.status || 'Scheduled'}
                              </span>
                            </td>
                          </tr>
                        );
                      });
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      );
    }

    // Fall back to CSV table view if no database data
    if (!parsedCsvForTable || parsedCsvForTable.length === 0) {
      return (
        <div className="empty-table-message">
          <p>📊 No schedule data found. Upload CSV data using Bulk Upload to populate the schedule.</p>
        </div>
      );
    }

    const headers = Object.keys(parsedCsvForTable[0]);
    const dataRows = parsedCsvForTable;

    // Extract week columns (skip first column which is day labels)
    const weekHeaders = headers.slice(1);
    const dayLabels = ['Day 1', 'Day 2', 'Day 3', 'Day 4', 'Day 5', 'Day 6'];

    return (
      <div style={{ width: '100%', overflowX: 'auto' }}>
        <div className="csv-table-container">
          <table className="csv-table">
            <thead>
              <tr>
                <th className="day-label">DAY</th>
                {weekHeaders.map((week, index) => (
                  <th key={index} className="week-header">{week}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dayLabels.map((day, dayIndex) => (
                <tr key={dayIndex}>
                  <td className="day-label">{day}</td>
                  {weekHeaders.map((week, weekIndex) => {
                    // Get content for this day/week combination from parsed CSV
                    const rowData = dataRows[dayIndex];
                    const content = rowData ? rowData[week] : '';

                    if (!content || !content.trim()) {
                      return <td key={weekIndex} className="empty-cell">-</td>;
                    }

                    return (
                      <td key={weekIndex} className="session-cell">
                        <div className="session-item">
                          {textWithBreaks(content)}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="management-page">
      {/* Modern Header Section with Controls */}
      <div className="page-header-with-controls">
        {/* Row 1: Title and Action Buttons */}
        <div className="header-row-top">
          <div className="page-header">
            <h1 className="page-title">
              <span className="page-title-icon">📅</span>{' '}
              {selectedBatchId
                ? `${batches.find(b => b.id === parseInt(selectedBatchId))?.batch_name || 'Batch'} Calendar`
                : 'Calendar'
              }
            </h1>
            <p className="page-subtitle">
              {selectedBatchId
                ? `Manage training sessions for ${batches.find(b => b.id === parseInt(selectedBatchId))?.center?.name || 'selected center'}`
                : 'Comprehensive training session management across all batches'
              }
            </p>
          </div>

          {/* Action Buttons */}
          <div className="header-actions">
            <select value={selectedBatchId} onChange={handleBatchChange} className="header-batch-select">
              <option value="">🌐 All Batches</option>
              {batches.map(batch => (
                <option key={batch.id} value={batch.id}>
                  📖 {batch.batch_name}
                </option>
              ))}
            </select>

            {isScheduler && (
              <button
                onClick={handleBulkUpload}
                className="bulk-upload-btn"
                disabled={!selectedBatchId}
              >
                📤 Upload
              </button>
            )}

            {isScheduler && (
              <button
                onClick={() => setShowHolidayModal(true)}
                className="holiday-btn"
              >
                📅 Holidays
              </button>
            )}

            {isScheduler && selectedBatchId && (
              <button
                onClick={handleRecalculateDates}
                className="recalculate-btn"
                title="Recalculate session dates based on batch start date and holidays"
              >
                🔄 Recalculate Dates
              </button>
            )}

            {/* Export Dropdown - shows different options based on view */}
            {((selectedBatchId && batchScheduleData.length > 0) ||
              (viewTab === 'center' && centerWiseData && centerWiseData.length > 0) ||
              (viewTab === 'zone' && zoneWiseData && zoneWiseData.length > 0)) && (
              <div className="export-dropdown">
                <button className="export-btn">
                  📥 Export
                </button>
                <div className="export-dropdown-content">
                  {selectedBatchId && batchScheduleData.length > 0 && (
                    <>
                      <button onClick={exportScheduleToExcel}>
                        📊 Full Schedule
                      </button>
                      <button onClick={exportTrainerWiseSchedule}>
                        👨‍🏫 Trainer-wise
                      </button>
                      <button onClick={exportWeekWiseSchedule}>
                        📅 Week-wise
                      </button>
                    </>
                  )}
                  {viewTab === 'center' && centerWiseData && centerWiseData.length > 0 && (
                    <button onClick={exportCenterWiseSchedule}>
                      🏢 Center-wise
                    </button>
                  )}
                  {viewTab === 'zone' && zoneWiseData && zoneWiseData.length > 0 && (
                    <>
                      <button onClick={exportZoneWiseSchedule}>
                        📍 Zone-wise (Excel)
                      </button>
                      <button onClick={printZoneWeeklyView}>
                        🖨️ Zone Weekly (PDF / Print)
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Row 2: View Tabs */}
        <div className="header-row-tabs">
          <div className="view-tabs">
            <button
              className={`view-tab-btn ${viewTab === 'calendar' ? 'active' : ''}`}
              onClick={() => handleViewTabChange('calendar')}
            >
              📅 Calendar
            </button>
            {selectedBatchId && (
              <>
                <button
                  className={`view-tab-btn ${viewTab === 'table' ? 'active' : ''}`}
                  onClick={() => handleViewTabChange('table')}
                >
                  📊 Table
                </button>
                <button
                  className={`view-tab-btn ${viewTab === 'week' ? 'active' : ''}`}
                  onClick={() => handleViewTabChange('week')}
                >
                  📆 Week
                </button>
              </>
            )}
            <button
              className={`view-tab-btn ${viewTab === 'trainer' ? 'active' : ''}`}
              onClick={() => handleViewTabChange('trainer')}
            >
              👨‍🏫 Trainer
            </button>
            <button
              className={`view-tab-btn ${viewTab === 'center' ? 'active' : ''}`}
              onClick={() => handleViewTabChange('center')}
            >
              🏢 Center
            </button>
            <button
              className={`view-tab-btn ${viewTab === 'zone' ? 'active' : ''}`}
              onClick={() => handleViewTabChange('zone')}
            >
              📍 Zone
            </button>
          </div>
        </div>
      </div>

      {/* Dashboard Statistics - Full Width Row */}
      {!loading && (
        <div className="stats-row fade-in">
          {renderDashboardStats()}
          {renderStatusLegend()}
        </div>
      )}

      {loading ? (
        <div className="loading-spinner fade-in">
          <div className="spinner"></div>
          <span>Loading your schedule data...</span>
        </div>
      ) : viewTab === 'table' && selectedBatchId ? (
        <div className="fade-in">{renderCsvTableView()}</div>
      ) : viewTab === 'week' && selectedBatchId ? (
        <div className="fade-in">{renderWeekWiseView()}</div>
      ) : viewTab === 'trainer' ? (
        <div className="fade-in">{renderTrainerWiseView()}</div>
      ) : viewTab === 'center' ? (
        <div className="fade-in">{renderCenterWiseView()}</div>
      ) : viewTab === 'zone' ? (
        <div className="fade-in">
          <ZoneWeeklyView
            zoneWiseData={zoneWiseData}
            trainerTasks={trainerTasks}
            trainers={trainers}
            weekStartDate={zoneWeekStart}
            onWeekChange={setZoneWeekStart}
            onTaskCreate={handleCreateTrainerTask}
            onTaskUpdate={handleUpdateTrainerTask}
            onTaskDelete={handleDeleteTrainerTask}
            onFacilitatorChange={handleZoneFacilitatorChange}
            isScheduler={isScheduler}
            savingSession={savingSession}
            includeCompleted={zoneFilters.includeCompleted}
            includeUpcoming={zoneFilters.includeUpcoming}
            onStatusFilterChange={setZoneFilters}
          />
        </div>
      ) : (
        <div className="calendar-container fade-in">
          <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,timeGridWeek,timeGridDay'
            }}
            initialView='dayGridMonth'
            events={events}
            selectable={isScheduler}
            editable={isScheduler}
            select={handleDateSelect}
            eventClick={handleEventClick}
            eventDrop={handleEventDrop}
            eventContent={renderEventContent}
            height="auto"
            eventDisplay="block"
            dayMaxEvents={5}
            moreLinkClick="popover"
          />
        </div>
      )}

      {/* Enhanced Bulk Upload Modal */}
      {showBulkUpload && (
        <div className="modal-backdrop">
          <div className="modal-content bulk-upload-modal">
            <button
              className="modal-close-btn"
              onClick={handleCloseBulkUpload}
              title="Close"
            >
              &times;
            </button>
            <h2>Bulk Upload Schedule</h2>

            {/* Excel File Upload Section */}
            <div className="excel-upload-section">
              <p><strong>📁 Option 1: Upload Excel File</strong></p>
              <div className="file-upload-container">
                <input
                  type="file"
                  id="excel-file-input"
                  accept=".xlsx,.xls"
                  onChange={handleExcelFileSelect}
                  className="file-input"
                />
                <label htmlFor="excel-file-input" className="file-input-label">
                  {uploadedFile ? `📄 ${uploadedFile.name}` : '📂 Choose Excel File (.xlsx, .xls)'}
                </label>
                {uploadedFile && (
                  <button type="button" className="clear-file-btn" onClick={handleClearExcelFile}>
                    ✕
                  </button>
                )}
              </div>
              {/* Sheet selector - shown when multiple sheets exist */}
              {excelSheetNames.length > 1 && (
                <div className="sheet-selector">
                  <label htmlFor="sheet-select">Select Worksheet:</label>
                  <select
                    id="sheet-select"
                    value={selectedSheet}
                    onChange={(e) => handleSheetSelect(e.target.value)}
                    className="sheet-select"
                  >
                    {excelSheetNames.map((name, index) => (
                      <option key={index} value={name}>{name}</option>
                    ))}
                  </select>
                </div>
              )}
              {excelError && <div className="excel-error">{excelError}</div>}
              {parsedExcelData && (
                <div className="excel-success">
                  ✅ Parsed {parsedExcelData.raw.length} sessions from "{parsedExcelData.sheetName}"
                </div>
              )}
            </div>

            <div className="upload-divider">
              <span>OR</span>
            </div>

            <div className="upload-instructions">
              <p><strong>📋 Option 2: Paste CSV Data</strong></p>
              <ol>
                <li>Copy your schedule data from Excel or Google Sheets</li>
                <li>Paste it in the text area below</li>
                <li>Format: Day labels in first column, weeks in subsequent columns</li>
              </ol>
            </div>
            <textarea
              value={csvData}
              onChange={(e) => {
                setCsvData(e.target.value);
                // Clear Excel state if manually editing
                if (parsedExcelData) {
                  setParsedExcelData(null);
                  setUploadedFile(null);
                }
              }}
              placeholder="📊 Paste your CSV data here...\n\nExample format:\nDay,Week 1,Week 2,Week 3\nDay 1,Session 1,Session 4,Session 7\nDay 2,Session 2,Session 5,Session 8\n..."
              rows={10}
            />
            <div className="modal-actions">
              <button type="button" onClick={handleCloseBulkUpload}>❌ Cancel</button>
              <button
                type="button"
                onClick={handleCsvUpload}
                disabled={loading || !csvData.trim()}
                className={csvData.trim() ? 'ready-to-upload' : ''}
              >
                {loading ? '⏳ Uploading...' : csvData.trim() ? '✅ Upload Schedule' : '📤 Upload Schedule'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Enhanced Session Modal */}
      {isModalOpen && (
        <div className="modal-backdrop">
          <div className="modal-content">
            <h2>{isEditing ? '✏️ Edit Session' : '➕ Add New Session'}</h2>
            <form onSubmit={handleSubmit}>
              <div>
                <label>📚 Batch:</label>
                <select name="batchId" value={formData.batchId} onChange={handleBatchChangeInModal} required>
                  <option value="">-- Select Batch --</option>
                  {batches.map(b => <option key={b.id} value={b.id}>{b.batch_name}</option>)}
                </select>
              </div>

              <div>
                <label>📖 Course/Module:</label>
                <select name="moduleId" value={formData.moduleId} onChange={handleInputChange} required disabled={!formData.batchId}>
                  <option value="">-- Select Course --</option>
                  {availableModules.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
              
              <div>
                <label>👨‍🏫 Teacher/Trainer:</label>
                <select name="trainerId" value={formData.trainerId} onChange={handleInputChange} required>
                  <option value="">-- Select Teacher --</option>
                  {trainers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>

              <div>
                <label>📅 Date & Time:</label>
                <input type="datetime-local" name="session_date" value={formData.session_date ? formData.session_date.substring(0, 16) : ''} onChange={handleInputChange} required />
              </div>
              
              <div>
                <label>📊 Status:</label>
                <select name="status" value={formData.status} onChange={handleInputChange}>
                  <option value="Draft">📝 Draft</option>
                  <option value="Published">📢 Published</option>
                  <option value="Completed">✅ Completed</option>
                  <option value="Cancelled">❌ Cancelled</option>
                </select>
              </div>
              
              <div className="modal-actions">
                {isEditing && <button type="button" className="delete" onClick={handleDelete}>🗑️ Delete</button>}
                <button type="button" onClick={handleCloseModal}>❌ Cancel</button>
                <button type="submit">{isEditing ? '💾 Save Changes' : '➕ Create Session'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Holiday Management Modal */}
      {showHolidayModal && (
        <div className="modal-backdrop">
          <div className="modal-content holiday-modal">
            <div className="modal-header">
              <h2>📅 Holiday Management</h2>
              <button className="modal-close" onClick={() => setShowHolidayModal(false)}>×</button>
            </div>

            <div className="holiday-year-selector">
              <label>Year:</label>
              <select
                value={holidayYear}
                onChange={(e) => {
                  setHolidayYear(parseInt(e.target.value));
                  fetchHolidays(parseInt(e.target.value));
                }}
              >
                {[...Array(5)].map((_, i) => {
                  const year = new Date().getFullYear() + i - 1;
                  return <option key={year} value={year}>{year}</option>;
                })}
              </select>
            </div>

            <div className="holiday-upload-section">
              <div className="upload-header-row">
                <h3>Upload Holidays</h3>
                <button
                  className="load-indian-holidays-btn"
                  onClick={loadIndianHolidays}
                  type="button"
                >
                  🇮🇳 Load Indian Holidays
                </button>
              </div>
              <p className="upload-hint">
                Upload CSV or Excel file, or click "Load Indian Holidays" for default list
              </p>
              <p className="upload-hint">
                Date format: YYYY-MM-DD (e.g., 2025-01-26)
              </p>

              <div className="file-upload-row">
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleHolidayFileSelect}
                  id="holiday-file-input"
                />
                <label htmlFor="holiday-file-input" className="file-upload-label">
                  {holidayFile ? holidayFile.name : 'Choose File'}
                </label>
              </div>

              <textarea
                className="holiday-csv-input"
                placeholder="Or paste holiday data here (CSV format):&#10;Date,Name,Description&#10;2025-01-26,Republic Day,National Holiday&#10;2025-08-15,Independence Day,National Holiday"
                value={holidayCsvData}
                onChange={(e) => setHolidayCsvData(e.target.value)}
                rows={6}
              />

              <button
                className="upload-btn"
                onClick={handleHolidayUpload}
                disabled={loading || !holidayCsvData.trim()}
              >
                {loading ? '⏳ Uploading...' : '📤 Upload Holidays'}
              </button>
            </div>

            <div className="holiday-list-section">
              <h3>Holidays for {holidayYear} ({holidays.length})</h3>
              {holidays.length > 0 ? (
                <table className="holiday-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Name</th>
                      <th>Description</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {holidays.map(holiday => (
                      <tr key={holiday.id}>
                        <td>{holiday.date}</td>
                        <td>{holiday.name}</td>
                        <td>{holiday.description || '-'}</td>
                        <td>
                          <button
                            className="delete-btn-small"
                            onClick={() => handleDeleteHoliday(holiday.id)}
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="no-holidays">No holidays defined for {holidayYear}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Batch Schedule Edit Modal - for Calendar View Facilitator Assignment */}
      {showScheduleEditModal && selectedScheduleEntry && (
        <div className="modal-backdrop">
          <div className="modal-content schedule-edit-modal">
            <button
              className="modal-close-btn"
              onClick={handleCloseScheduleEditModal}
              title="Close"
            >
              &times;
            </button>
            <h2>Edit Session</h2>

            <div className="schedule-edit-details">
              <div className="detail-row">
                <span className="detail-label">Session:</span>
                <span className="detail-value">{selectedScheduleEntry.session_content}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Week:</span>
                <span className="detail-value">Week {selectedScheduleEntry.week_number}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Day:</span>
                <span className="detail-value">Day {selectedScheduleEntry.day_number}</span>
              </div>
              {selectedScheduleEntry.session_date && (
                <div className="detail-row">
                  <span className="detail-label">Date:</span>
                  <span className="detail-value">
                    {parseLocalDate(selectedScheduleEntry.session_date)?.toLocaleDateString('en-IN', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </div>
              )}
            </div>

            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              handleScheduleEntryUpdate({
                trainerId: formData.get('trainerId'),
                status: formData.get('status')
              });
            }}>
              <div className="form-group">
                <label>👨‍🏫 Facilitator:</label>
                {(() => {
                  const availableFacilitators = getAvailableFacilitators(
                    selectedScheduleEntry.session_date,
                    selectedScheduleEntry.id
                  );
                  const busyCount = trainers.length - availableFacilitators.length;
                  const onLeaveCount = getTrainersOnLeaveCount(selectedScheduleEntry.session_date);

                  return (
                    <>
                      <select
                        name="trainerId"
                        defaultValue={selectedScheduleEntry.trainerId || ''}
                        className="facilitator-select-modal"
                      >
                        <option value="">-- Select Facilitator --</option>
                        {/* Show currently assigned trainer even if busy elsewhere */}
                        {selectedScheduleEntry.trainerId &&
                          !availableFacilitators.find(t => t.id === selectedScheduleEntry.trainerId) && (
                            <option value={selectedScheduleEntry.trainerId}>
                              {selectedScheduleEntry.trainer?.name || 'Current Trainer'} (currently assigned)
                            </option>
                          )}
                        {availableFacilitators.map(trainer => (
                          <option key={trainer.id} value={trainer.id}>
                            {trainer.name}
                          </option>
                        ))}
                      </select>
                      {busyCount > 0 && (
                        <small className="conflict-hint-modal">
                          {busyCount} facilitator{busyCount > 1 ? 's' : ''} unavailable
                          {onLeaveCount > 0 && ` (${onLeaveCount} on leave)`}
                        </small>
                      )}
                    </>
                  );
                })()}
              </div>

              <div className="form-group">
                <label>📊 Status:</label>
                <select
                  name="status"
                  defaultValue={selectedScheduleEntry.status || 'scheduled'}
                  className="status-select-modal"
                >
                  <option value="scheduled">Scheduled</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="rescheduled">Rescheduled</option>
                </select>
              </div>

              <div className="modal-actions">
                <button type="button" onClick={handleCloseScheduleEditModal}>
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingSession === selectedScheduleEntry.id}
                  className={savingSession === selectedScheduleEntry.id ? 'saving' : ''}
                >
                  {savingSession === selectedScheduleEntry.id ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* F1: Insert-and-auto-shift modal */}
      {insertModal && (
        <div className="modal-overlay" onClick={closeInsertModal}>
          <div className="modal-content insert-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>➕ Insert session — Week {insertModal.week}, Day {insertModal.day}</h3>
              <button className="modal-close" onClick={closeInsertModal}>✕</button>
            </div>
            <div className="modal-form">
              {insertError && (
                <div className="form-error-banner">⚠️ {insertError}</div>
              )}

              <div className="form-field">
                <label>Session content</label>
                <textarea
                  rows={3}
                  value={insertForm.session_content}
                  onChange={(e) => setInsertForm(f => ({ ...f, session_content: e.target.value }))}
                  placeholder="e.g., Guest talk by volunteer speaker, Expert panel on career paths"
                />
              </div>

              <div className="form-field">
                <label>Facilitator (optional)</label>
                <select
                  value={insertForm.trainerId}
                  onChange={(e) => setInsertForm(f => ({ ...f, trainerId: e.target.value }))}
                >
                  <option value="">-- None --</option>
                  {trainers.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>

              {insertPreview && (
                <div className={`insert-preview ${insertPreview.needsExtension ? 'overflow' : ''}`}>
                  {insertPreview.needsExtension ? (
                    <>
                      <strong>⚠️ Batch overflow</strong>
                      <p>
                        Inserting here will push <strong>{insertPreview.overflowCount}</strong> session(s)
                        beyond the program's {insertPreview.frameworkMaxWeek}-week framework.
                        Total shift: {insertPreview.affectedCount} session(s).
                        New batch end: <strong>{insertPreview.suggestedEndDate}</strong>.
                      </p>
                    </>
                  ) : (
                    <p>
                      {insertPreview.affectedCount > 0
                        ? <><strong>{insertPreview.affectedCount}</strong> session(s) will shift one slot forward.</>
                        : <>No existing session will be shifted — this slot is empty.</>}
                      {insertPreview.lastNewDate && (
                        <> Last session will fall on <strong>{insertPreview.lastNewDate}</strong>.</>
                      )}
                    </p>
                  )}
                </div>
              )}

              <div className="modal-actions">
                <button type="button" onClick={closeInsertModal} disabled={insertSubmitting}>
                  Cancel
                </button>
                {insertPreview?.needsExtension ? (
                  <button
                    type="button"
                    onClick={() => submitInsert(true)}
                    disabled={insertSubmitting || !insertForm.session_content.trim()}
                  >
                    {insertSubmitting ? 'Extending & inserting…' : `Confirm: extend batch & insert`}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => submitInsert(false)}
                    disabled={insertSubmitting || !insertForm.session_content.trim()}
                  >
                    {insertSubmitting ? 'Inserting…' : 'Insert session'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default SchedulerPage;